import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { VendorAPI } from '../lib/b2bApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { MobileTabNav } from '../components/ui/mobile-tab-nav';
import NotificationBell from '../components/NotificationBell';
import { requestNotificationPermission, registerPushNotifications } from '../lib/pushNotifications';
import DownloadAppButton from '../components/DownloadAppButton';
import VendorWalletTab from './VendorWalletTab';
import VendorLinkDoctors from './VendorLinkDoctors';
import WalletRedemptionSection from './WalletRedemptionSection';
import ChemistBillingTab from './ChemistBillingTab';
import ChemistInventoryTab from './ChemistInventoryTab';
import VendorLinkVendors from './VendorLinkVendors';
import ChemistMarketplace from './ChemistMarketplace';
import SavedAddresses from './SavedAddresses';
import VendorServiceSettings from './VendorServiceSettings';
import VendorReferralLedger from './VendorReferralLedger';
import VendorBookForDoctorTab from '../components/vendor/VendorBookForDoctorTab';
import { InvoiceUploader, ReviewSection } from '../components/ReviewsAndInvoices';
import {
  LogOut, Plus, Edit2, Trash2, ShoppingCart, IndianRupee, Package,
  FlaskConical, Pill, Clock, CheckCircle, Building2, Search, Filter,
  Upload, FileText, Download, Phone, Mail, MapPin, User, MessageCircle,
  HelpCircle, Tag, Pencil, Image, Loader2, UserCircle, Save, Briefcase,
  Navigation, Users, RefreshCw, AlertCircle, ExternalLink, Calendar, X,
  CreditCard, TrendingUp, QrCode, Menu, Wrench, AlertTriangle, RotateCcw, Receipt, Stethoscope, ClipboardList
} from 'lucide-react';

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [chemistB2BOrders, setChemistB2BOrders] = useState([]);
  const [chemistPatientOrders, setChemistPatientOrders] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [reports, setReports] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParamsV, setSearchParamsV] = useSearchParams();
  const [activeTab, setActiveTabRaw] = useState(searchParamsV.get('tab') || 'catalog');
  const setActiveTab = useCallback((val) => {
    setActiveTabRaw(val);
    setSearchParamsV(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'catalog') next.delete('tab');
      else next.set('tab', val);
      return next;
    }, { replace: true });
  }, [setSearchParamsV]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showUploadReport, setShowUploadReport] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Vendor wallet balance for header
  const [vendorWalletBalance, setVendorWalletBalance] = useState(0);
  
  // Doctor search filters for order sections
  const [b2bDoctorSearch, setB2bDoctorSearch] = useState('');
  const [patientOrderDoctorSearch, setPatientOrderDoctorSearch] = useState('');
  const [b2bFilteredSummary, setB2bFilteredSummary] = useState(null);
  const [patientOrderFilteredSummary, setPatientOrderFilteredSummary] = useState(null);
  
  // Support ticket state
  const [supportTickets, setSupportTickets] = useState([]);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', category: 'general', priority: 'normal' });
  const [replyText, setReplyText] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  
  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: 0,
    min_order_value: 0, max_uses: 0, valid_until: '', is_active: true,
    applicable_tiers: []
  });
  
  // Invoice upload state
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  
  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  
  // Multiple report upload state
  const [multipleReportFiles, setMultipleReportFiles] = useState([]);
  const [uploadingMultipleReports, setUploadingMultipleReports] = useState(false);
  
  // Platform Support Contacts (from Admin)
  const [platformSupport, setPlatformSupport] = useState(null);
  
  // Photo lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  
  // Marketing orders state (for marketing vendors)
  const [marketingOrders, setMarketingOrders] = useState([]);
  
  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '', email: '', phone: '', address: '',
    gst_number: '', gst_percentage: '', license_number: '',
    mr_name: '', mr_phone: '', mr_email: '', mr_territory: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // MR Visits state (Real-time monitoring)
  const [mrVisitsStats, setMrVisitsStats] = useState({ today: 0, this_month: 0, total: 0, verified_today: 0, top_clinics: [], top_mrs: [] });
  const [mrLiveVisits, setMrLiveVisits] = useState({ active_visits: [], recent_completed: [] });
  const [mrVisitsLoading, setMrVisitsLoading] = useState(false);
  const [mrAutoRefresh, setMrAutoRefresh] = useState(true);
  
  // Vendor Banners state
  const [vendorBanners, setVendorBanners] = useState([]);
  const [uploadingVBanner, setUploadingVBanner] = useState(false);
  const [vBannerTitle, setVBannerTitle] = useState('');
  const vBannerFileRef = useRef(null);
  
  // Order filters
  const [orderFilters, setOrderFilters] = useState({
    status: 'all', clinic_id: 'all', patient_name: '', search: '', date_from: '', date_to: ''
  });
  
  // Patient Portal Orders state
  const [patientPortalOrders, setPatientPortalOrders] = useState([]);
  const [patientPortalOrdersLoading, setPatientPortalOrdersLoading] = useState(false);
  const [patientPortalOrderFilters, setPatientPortalOrderFilters] = useState({
    status: 'all', search: '', date_from: '', date_to: ''
  });

  // Report upload state
  const [reportUpload, setReportUpload] = useState({
    order_id: '', patient_id: '', patient_name: '', patient_phone: '', report_title: '', file: null
  });

  // My Doctors (Vendor-Doctor Mapping) state
  const [myDoctors, setMyDoctors] = useState([]);
  const [pendingDoctorRequests, setPendingDoctorRequests] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [showRequestDoctorDialog, setShowRequestDoctorDialog] = useState(false);
  const [requestDoctorNotes, setRequestDoctorNotes] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [requestingDoctor, setRequestingDoctor] = useState(false);
  const [productVisibilityDialog, setProductVisibilityDialog] = useState({ open: false, doctor: null, products: [] });
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productVisibilityLoading, setProductVisibilityLoading] = useState(false);

  // Credit settings state
  const [creditDialog, setCreditDialog] = useState({ open: false, mapping: null });
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLimit, setCreditLimit] = useState('');
  const [creditPeriodDays, setCreditPeriodDays] = useState('30');
  const [savingCredit, setSavingCredit] = useState(false);
  const [savingProductVisibility, setSavingProductVisibility] = useState(false);

  // Expiry Alerts state
  const [expiryAlerts, setExpiryAlerts] = useState({ expiring_soon: [], expired: [], summary: {} });
  const [expiryAlertsLoading, setExpiryAlertsLoading] = useState(false);

  // Earnings & Payouts state
  const [earningsData, setEarningsData] = useState(null);
  const [earningsOrders, setEarningsOrders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [showAddBankDialog, setShowAddBankDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: '', account_number: '', ifsc_code: '',
    bank_name: '', account_type: 'savings', is_primary: true
  });
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  // Earnings filter state
  const [earningsSearch, setEarningsSearch] = useState('');
  const [earningsStatusFilter, setEarningsStatusFilter] = useState('all');
  const [earningsPayoutFilter, setEarningsPayoutFilter] = useState('all');
  const [earningsOrderTypeFilter, setEarningsOrderTypeFilter] = useState('all');
  const [earningsPaymentFilter, setEarningsPaymentFilter] = useState('all');
  const [earningsSummary, setEarningsSummary] = useState(null);
  const [earningsDateFrom, setEarningsDateFrom] = useState('');
  const [earningsDateTo, setEarningsDateTo] = useState('');
  const [submittingBank, setSubmittingBank] = useState(false);
  const [submittingPayout, setSubmittingPayout] = useState(false);
  // Payout history filter state
  const [payoutDateFrom, setPayoutDateFrom] = useState('');
  const [payoutDateTo, setPayoutDateTo] = useState('');
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('all');
  // UPI QR state
  const [vendorUpiQr, setVendorUpiQr] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('vendor_token');
    const vendorData = localStorage.getItem('vendor_user');
    if (!token) {
      navigate('/vendor');
      return;
    }
    if (vendorData) setVendor(JSON.parse(vendorData));
    fetchData();
    fetchVendorBanners();
    // Register push notifications
    requestNotificationPermission().then(perm => {
      if (perm === 'granted') registerPushNotifications('vendor');
    });

    const interval = setInterval(() => { if (!document.hidden) fetchData(); }, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const vendorData = JSON.parse(localStorage.getItem('vendor_user') || '{}');
    try {
      const endpoint = getProductEndpoint(vendorData.vendor_type);
      const promises = [
        VendorAPI.get(`${endpoint}/my-${getProductType(vendorData.vendor_type)}`),
        VendorAPI.get('/orders/vendor-orders'),
      ];
      
      // Labs get additional patient search and reports endpoints
      if (vendorData.vendor_type === 'lab') {
        promises.push(VendorAPI.get('/reports/vendor-reports').catch(() => ({ data: [] })));
        promises.push(VendorAPI.get('/reports/search-patients').catch(() => ({ data: { patients: [], clinics: [] } })));
      }
      
      const results = await Promise.all(promises);
      setProducts(results[0].data);
      
      // Handle the orders response format
      const ordersData = results[1].data;
      if (ordersData.orders) {
        setOrders(ordersData.orders);
        setClinics(ordersData.clinics || []);
      } else {
        setOrders(ordersData);
      }
      
      if (vendorData.vendor_type === 'lab') {
        setReports(results[2]?.data || []);
        const patientData = results[3]?.data || { patients: [], clinics: [] };
        setPatients(patientData.patients || []);
        if (!clinics.length && patientData.clinics) {
          setClinics(patientData.clinics);
        }
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
    
    // Fetch vendor wallet balance for header
    try {
      const token = localStorage.getItem('vendor_token');
      const walletRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-wallet/balance`, { headers: { Authorization: `Bearer ${token}` }});
      setVendorWalletBalance(walletRes.data?.balance || 0);
    } catch (e) { /* wallet may not exist yet */ }

    // Auto-fetch marketing orders for marketing vendors
    if (vendorData.vendor_type === 'marketing') {
      try {
        const res = await VendorAPI.get('/marketing-services/vendor-orders');
        setMarketingOrders(res.data || []);
      } catch (err) { console.error(err); }
    }
  }, []);

  // Vendor Banners
  const fetchVendorBanners = useCallback(async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banners/vendor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorBanners(res.data.banners || []);
    } catch (err) { console.error(err); }
  }, []);

  const uploadVendorBanner = async (file) => {
    if (!file) return;
    setUploadingVBanner(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', vBannerTitle);
      formData.append('sort_order', vendorBanners.length);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/banners/vendor`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Banner uploaded');
      setVBannerTitle('');
      fetchVendorBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally { setUploadingVBanner(false); }
  };

  const deleteVendorBanner = async (bannerId) => {
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/banners/vendor/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchVendorBanners();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchFilteredOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (orderFilters.status && orderFilters.status !== 'all') params.append('status', orderFilters.status);
      if (orderFilters.clinic_id && orderFilters.clinic_id !== 'all') params.append('clinic_id', orderFilters.clinic_id);
      if (orderFilters.patient_name) params.append('patient_name', orderFilters.patient_name);
      if (orderFilters.search) params.append('search', orderFilters.search);
      if (orderFilters.date_from) params.append('date_from', orderFilters.date_from);
      if (orderFilters.date_to) params.append('date_to', orderFilters.date_to);
      if (orderFilters.buyer_type && orderFilters.buyer_type !== 'all') params.append('buyer_type', orderFilters.buyer_type);
      if (orderFilters.payment_method && orderFilters.payment_method !== 'all') params.append('payment_method', orderFilters.payment_method);
      if (orderFilters.order_type && orderFilters.order_type !== 'all') params.append('order_type', orderFilters.order_type);
      
      const res = await VendorAPI.get(`/orders/vendor-orders?${params.toString()}`);
      const ordersData = res.data;
      if (ordersData.orders) {
        setOrders(ordersData.orders);
        setClinics(ordersData.clinics || clinics);
      } else {
        setOrders(ordersData);
      }
    } catch (err) {
      toast.error('Failed to filter orders');
    }
  };

  const fetchChemistB2BOrders = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const params = new URLSearchParams({ buyer_type: 'chemist' });
      if (b2bDoctorSearch) params.append('doctor_search', b2bDoctorSearch);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/orders/vendor-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChemistB2BOrders(Array.isArray(res.data) ? res.data : res.data.orders || []);
      setB2bFilteredSummary(res.data.filtered_summary || null);
    } catch (err) { console.error(err); }
  };

  const fetchChemistPatientOrders = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patient-marketplace/vendor/orders?buyer_type=chemist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChemistPatientOrders(Array.isArray(res.data) ? res.data : res.data.orders || []);
    } catch (err) { console.error(err); }
  };

  const getProductEndpoint = (type) => {
    switch (type) {
      case 'lab': return '/lab-tests';
      case 'pharma': return '/medicines';
      case 'chemist': return '/medicines';
      case 'inventory': return '/inventory';
      case 'marketing': return '/marketing-services';
      default: return '';
    }
  };

  const getProductType = (type) => {
    switch (type) {
      case 'lab': return 'tests';
      case 'pharma': return 'medicines';
      case 'chemist': return 'medicines';
      case 'inventory': return 'products';
      case 'marketing': return 'services';
      default: return 'items';
    }
  };

  const getVendorIcon = (type) => {
    switch (type) {
      case 'lab': return <FlaskConical className="w-6 h-6" />;
      case 'pharma': return <Pill className="w-6 h-6" />;
      case 'chemist': return <Pill className="w-6 h-6" />;
      case 'inventory': return <Package className="w-6 h-6" />;
      case 'marketing': return <TrendingUp className="w-6 h-6" />;
      default: return <Building2 className="w-6 h-6" />;
    }
  };

  const getVendorColor = (type) => {
    switch (type) {
      case 'lab': return 'from-cyan-600 to-blue-600';
      case 'pharma': return 'from-pink-600 to-rose-600';
      case 'chemist': return 'from-emerald-600 to-teal-600';
      case 'inventory': return 'from-orange-600 to-amber-600';
      case 'marketing': return 'from-violet-600 to-purple-600';
      default: return 'from-teal-600 to-emerald-600';
    }
  };

  const handleOrderAction = async (orderId, status, notes = '') => {
    try {
      await VendorAPI.put(`/orders/vendor-orders/${orderId}/status`, { status, notes });
      toast.success(`Order ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const [markPaidOrder, setMarkPaidOrder] = useState(null);
  const [markPaidMethod, setMarkPaidMethod] = useState('cash');
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);

  const handleMarkCreditPaid = async () => {
    if (!markPaidOrder) return;
    setMarkingPaid(true);
    try {
      const res = await VendorAPI.post(`/orders/vendor/${markPaidOrder.id}/mark-paid`, { method: markPaidMethod, note: markPaidNote });
      toast.success(res.data?.message || 'Marked as paid');
      setMarkPaidOrder(null);
      setMarkPaidNote('');
      setMarkPaidMethod('cash');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const endpoint = getProductEndpoint(vendor.vendor_type);
      await VendorAPI.delete(`${endpoint}/${productId}`);
      toast.success('Item deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const toggleSelectProduct = (id) => setSelectedProducts(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleBulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm(`Delete ${selectedProducts.length} selected item(s)?`)) return;
    try {
      const endpoint = getProductEndpoint(vendor.vendor_type);
      await Promise.all(selectedProducts.map(id => VendorAPI.delete(`${endpoint}/${id}`)));
      toast.success(`${selectedProducts.length} item(s) deleted`);
      setSelectedProducts([]);
      fetchData();
    } catch (err) {
      toast.error('Bulk delete failed');
    }
  };

  const uploadLabReport = async () => {
    if (!reportUpload.file || !reportUpload.order_id || !reportUpload.report_title) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const formData = new FormData();
    formData.append('order_id', reportUpload.order_id);
    formData.append('report_title', reportUpload.report_title);
    formData.append('patient_name', reportUpload.patient_name);
    formData.append('patient_id', reportUpload.patient_id || '');
    formData.append('patient_phone', reportUpload.patient_phone || '');
    formData.append('file', reportUpload.file);
    
    try {
      await VendorAPI.post('/reports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Report uploaded successfully');
      setShowUploadReport(false);
      setReportUpload({ order_id: '', patient_id: '', patient_name: '', patient_phone: '', report_title: '', file: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await VendorAPI.delete(`/reports/${reportId}`);
      toast.success('Report deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const shareOrderWhatsApp = (order) => {
    const items = order.items?.map(i => `• ${i.item_name} x${i.quantity}`).join('\n') || '';
    const msg = `*Order Details*\nOrder #: ${order.order_number}\nClinic: ${order.clinic_name}\n\n*Items:*\n${items}\n\nTotal: ₹${order.total}\nStatus: ${order.status}\n\nPatient: ${order.patient_name || 'N/A'}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const confirmLogout = useCallback(() => {
    localStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_user');
    setShowLogoutDialog(false);
    navigate('/vendor');
    toast.success('Logged out successfully');
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  const cancelLogout = useCallback(() => {
    setShowLogoutDialog(false);
  }, []);

  // Prevent back button from logging out - push state to history
  useEffect(() => {
    const token = localStorage.getItem('vendor_token');
    if (!token) return;

    const handlePopState = () => {
      // If user presses back and would go to login page, prevent it
      if (window.location.pathname.includes('/vendor')) {
        // Push current state back to prevent leaving the app
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Initial push to prevent back from going to login
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Expiry Alerts for Vendor (products at clinics that are expiring)
  const fetchExpiryAlerts = async () => {
    setExpiryAlertsLoading(true);
    try {
      const res = await VendorAPI.get('/clinic-inventory/vendor-expiry-alerts');
      setExpiryAlerts(res.data);
    } catch (err) {
      console.error('Failed to fetch expiry alerts:', err);
      // Silently fail - might not have inventory at clinics yet
    } finally {
      setExpiryAlertsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getNextStatus = (currentStatus) => {
    const flow = { pending: 'confirmed', confirmed: 'processing', processing: 'shipped', shipped: 'delivered' };
    return flow[currentStatus];
  };

  // Support ticket functions
  const fetchSupportTickets = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const [ticketsRes, supportRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/tickets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/public/support-contacts/vendor`)
      ]);
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setSupportTickets(data);
      }
      if (supportRes.ok) {
        const supportData = await supportRes.json();
        setPlatformSupport(supportData);
      }
    } catch (err) {
      console.error('Failed to fetch tickets', err);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setTicketLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ticketForm)
      });
      if (res.ok) {
        toast.success('Support ticket created');
        setShowCreateTicket(false);
        setTicketForm({ subject: '', message: '', category: 'general', priority: 'normal' });
        fetchSupportTickets();
      } else {
        throw new Error('Failed');
      }
    } catch (err) {
      toast.error('Failed to create ticket');
    } finally {
      setTicketLoading(false);
    }
  };

  const handleTicketReply = async () => {
    if (!replyText.trim()) return;
    setTicketLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/tickets/${viewTicket.id}/reply`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyText })
      });
      if (res.ok) {
        const data = await res.json();
        setViewTicket(data);
        setReplyText('');
        fetchSupportTickets();
        toast.success('Reply sent');
      } else {
        throw new Error('Failed');
      }
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setTicketLoading(false);
    }
  };

  // Fetch latest vendor profile
  const fetchVendorProfile = async () => {
    try {
      const res = await VendorAPI.get('/vendors/me');
      setVendor(res.data);
      localStorage.setItem('vendor_user', JSON.stringify(res.data));
      // Update profile form with latest data
      setProfileForm({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        mr_name: res.data.mr_name || '',
        mr_phone: res.data.mr_phone || '',
        mr_email: res.data.mr_email || '',
        mr_territory: res.data.mr_territory || ''
      });
    } catch (err) {
      console.error('Failed to fetch vendor profile', err);
    }
  };

  // MR Visits functions (Real-time monitoring)
  const fetchMrVisitsStats = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mr-checkin/vendor-visits/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMrVisitsStats(res.data);
    } catch (err) {
      console.error('Failed to fetch MR visits stats', err);
    }
  };

  const fetchMrLiveVisits = async () => {
    setMrVisitsLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mr-checkin/vendor-visits/live`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMrLiveVisits(res.data);
    } catch (err) {
      console.error('Failed to fetch live MR visits', err);
    } finally {
      setMrVisitsLoading(false);
    }
  };

  const fetchMrVisitsData = async () => {
    await Promise.all([fetchMrVisitsStats(), fetchMrLiveVisits()]);
  };

  // Fetch Patient Portal Orders
  const fetchPatientPortalOrders = async () => {
    setPatientPortalOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (patientPortalOrderFilters.status !== 'all') params.append('status', patientPortalOrderFilters.status);
      if (patientPortalOrderFilters.search) params.append('search', patientPortalOrderFilters.search);
      if (patientPortalOrderFilters.date_from) params.append('date_from', patientPortalOrderFilters.date_from);
      if (patientPortalOrderFilters.date_to) params.append('date_to', patientPortalOrderFilters.date_to);
      if (patientOrderDoctorSearch) params.append('doctor_search', patientOrderDoctorSearch);
      
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patient-marketplace/vendor/orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('vendor_token')}` }
      });
      setPatientPortalOrders(res.data.orders || []);
      setPatientOrderFilteredSummary(res.data.filtered_summary || null);
    } catch (err) {
      console.error('Failed to fetch patient portal orders', err);
      toast.error('Failed to load patient orders');
    } finally {
      setPatientPortalOrdersLoading(false);
    }
  };

  // Update Patient Portal Order Status
  const updatePatientPortalOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/patient-marketplace/vendor/orders/${orderId}/status?status=${status}`, 
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('vendor_token')}` }}
      );
      toast.success(`Order status updated to ${status}`);
      fetchPatientPortalOrders();
    } catch (err) {
      toast.error('Failed to update order status');
    }
  };

  // Auto-refresh MR visits every 30 seconds
  useEffect(() => {
    let interval;
    if (activeTab === 'mr-visits' && mrAutoRefresh) {
      interval = setInterval(fetchMrLiveVisits, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, mrAutoRefresh]);

  // Handle QR prescription scan redirect for chemists
  const [pendingRxBarcode, setPendingRxBarcode] = useState(null);
  useEffect(() => {
    const rxBarcode = searchParamsV.get('rx') || localStorage.getItem('pending_rx_barcode');
    if (rxBarcode && vendor?.vendor_type === 'chemist') {
      localStorage.removeItem('pending_rx_barcode');
      setPendingRxBarcode(rxBarcode);
      setActiveTab('walkin-billing');
      // Clean rx from URL
      setSearchParamsV(prev => {
        const next = new URLSearchParams(prev);
        next.delete('rx');
        next.set('tab', 'walkin-billing');
        return next;
      }, { replace: true });
    }
  }, [vendor, searchParamsV]);


  // Marketing orders fetch
  const fetchMarketingOrders = async () => {
    try {
      const res = await VendorAPI.get('/marketing-services/vendor-orders');
      setMarketingOrders(res.data || []);
    } catch (err) { console.error(err); }
  };

  // Earnings & Commission functions
  const fetchEarningsData = async (filters = {}) => {
    setEarningsLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const vendorData = JSON.parse(localStorage.getItem('vendor_user') || '{}');
      
      // Build query params for orders
      const orderParams = new URLSearchParams();
      orderParams.append('limit', '100');
      if (filters.search) orderParams.append('search', filters.search);
      if (filters.status && filters.status !== 'all') orderParams.append('status', filters.status);
      if (filters.payout_status && filters.payout_status !== 'all') orderParams.append('payout_status', filters.payout_status);
      if (filters.order_type && filters.order_type !== 'all') orderParams.append('order_type', filters.order_type);
      if (filters.date_from) orderParams.append('date_from', filters.date_from);
      if (filters.date_to) orderParams.append('date_to', filters.date_to);
      if (filters.payment_method && filters.payment_method !== 'all') orderParams.append('payment_method', filters.payment_method);
      if (filters.order_type && filters.order_type !== 'all') orderParams.append('order_type', filters.order_type);
      
      const headers = { Authorization: `Bearer ${token}` };

      if (vendorData.vendor_type === 'marketing') {
        // Marketing vendors: use marketing orders + bank + payouts
        const [mktOrdersRes, bankRes, payoutsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/marketing-services/vendor-orders`, { headers }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/bank-accounts`, { headers }).catch(() => ({ data: { accounts: [] } })),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/payout/my-requests`, { headers }).catch(() => ({ data: { payouts: [] } }))
        ]);
        const mktOrders = mktOrdersRes.data || [];
        // Transform to earnings format matching the table's expected fields
        const earningsOrds = mktOrders.map(o => ({
          ...o,
          order_id: o.id,
          total: o.price,
          subtotal: o.price,
          mrp: o.price,
          total_mrp: o.price,
          vendor_payout: o.net_vendor_payout,
          platform_commission: o.admin_commission,
          buyer_name: o.clinic_name,
          items: [{ item_name: o.service_name, quantity: 1, unit_price: o.price, total_price: o.price }],
        }));
        const totalRevenue = mktOrders.reduce((s, o) => s + (o.price || 0), 0);
        const totalCommission = mktOrders.reduce((s, o) => s + (o.admin_commission || 0), 0);
        const totalPayout = mktOrders.reduce((s, o) => s + (o.net_vendor_payout || 0), 0);
        setEarningsOrders(earningsOrds);
        setEarningsSummary({ total_orders: mktOrders.length, total_revenue: totalRevenue, total_mrp: totalRevenue, total_admin_commission: totalCommission, total_vendor_payout: totalPayout, total_received: 0, available_for_payout: totalPayout });
        setBankAccounts(bankRes.data.accounts || []);
        setPayoutRequests(payoutsRes.data.payouts || []);
      } else {
        const [ordersRes, bankRes, payoutsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/my-orders?${orderParams.toString()}`, { headers }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/bank-accounts`, { headers }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/payout/my-requests`, { headers })
        ]);
        setEarningsOrders(ordersRes.data.orders || []);
        setEarningsSummary(ordersRes.data.summary || null);
        setBankAccounts(bankRes.data.accounts || []);
        setPayoutRequests(payoutsRes.data.payouts || []);
      }
    } catch (err) {
      console.error('Failed to fetch earnings data', err);
    } finally {
      setEarningsLoading(false);
    }
  };
  
  const applyEarningsFilters = () => {
    fetchEarningsData({
      search: earningsSearch,
      status: earningsStatusFilter,
      payout_status: earningsPayoutFilter,
      date_from: earningsDateFrom,
      date_to: earningsDateTo,
      order_type: earningsOrderTypeFilter,
      payment_method: earningsPaymentFilter
    });
  };

  const resetEarningsFilters = () => {
    setEarningsSearch(''); setEarningsStatusFilter('all'); setEarningsPayoutFilter('all');
    setEarningsPaymentFilter('all'); setEarningsDateFrom(''); setEarningsDateTo('');
    setEarningsOrderTypeFilter('all'); fetchEarningsData();
  };

  // Upload UPI QR Code
  const uploadUpiQr = async (file) => {
    if (!file) return;
    setUploadingQr(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const formData = new FormData();
      formData.append('upi_qr', file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/upi-qr`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setVendorUpiQr(res.data.upi_qr_url);
      toast.success('UPI QR uploaded successfully');
    } catch (err) {
      // Handle different error formats
      let errorMsg = 'Failed to upload QR code';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Validation error';
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    } finally {
      setUploadingQr(false);
    }
  };

  const addBankAccount = async () => {
    if (!bankForm.account_holder_name || !bankForm.account_number || !bankForm.ifsc_code || !bankForm.bank_name) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmittingBank(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/bank-accounts`, bankForm, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Bank account added');
      setShowAddBankDialog(false);
      setBankForm({ account_holder_name: '', account_number: '', ifsc_code: '', bank_name: '', account_type: 'savings', is_primary: true });
      fetchEarningsData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bank account');
    } finally {
      setSubmittingBank(false);
    }
  };

  const deleteBankAccount = async (accountId) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/bank-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bank account deleted');
      fetchEarningsData();
    } catch (err) {
      toast.error('Failed to delete bank account');
    }
  };

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > (earningsSummary?.available_for_payout || 0)) {
      toast.error('Amount exceeds available balance');
      return;
    }
    if (bankAccounts.length === 0) {
      toast.error('Please add a bank account first');
      return;
    }
    setSubmittingPayout(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/payout/request`, 
        { amount, notes: payoutNotes },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Payout request submitted! Admin will process it shortly.');
      setShowPayoutDialog(false);
      setPayoutAmount('');
      setPayoutNotes('');
      fetchEarningsData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payout request');
    } finally {
      setSubmittingPayout(false);
    }
  };

  // Coupon functions
  const fetchCoupons = async () => {
    try {
      const res = await VendorAPI.get('/coupons/my-coupons');
      setCoupons(res.data);
    } catch (err) {
      console.error('Failed to fetch coupons', err);
    }
  };

  const handleSaveCoupon = async (e) => {
    e.preventDefault();
    try {
      if (editingCoupon) {
        await VendorAPI.put(`/coupons/${editingCoupon.id}`, couponForm);
        toast.success('Coupon updated');
      } else {
        await VendorAPI.post('/coupons', couponForm);
        toast.success('Coupon created');
      }
      setShowCouponDialog(false);
      setEditingCoupon(null);
      setCouponForm({ code: '', description: '', discount_type: 'percentage', discount_value: 0, min_order_value: 0, max_uses: 0, valid_until: '', is_active: true, applicable_tiers: [] });
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await VendorAPI.delete(`/coupons/${couponId}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order_value: coupon.min_order_value || 0,
      max_uses: coupon.max_uses || 0,
      valid_until: coupon.valid_until || '',
      is_active: coupon.is_active,
      applicable_tiers: coupon.applicable_tiers || []
    });
    setShowCouponDialog(true);
  };

  // Bulk import function
  const handleBulkImport = async (file) => {
    if (!file) return;
    setBulkImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = vendor?.vendor_type === 'lab' ? '/bulk-import/lab-tests' :
                       vendor?.vendor_type === 'pharma' ? '/bulk-import/medicines' : 
                       vendor?.vendor_type === 'chemist' ? '/bulk-import/medicines' : '/bulk-import/inventory';
      const res = await VendorAPI.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} items`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      setShowBulkImport(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBulkImportLoading(false);
    }
  };

  // Download bulk import template
  const downloadBulkTemplate = () => {
    const type = vendor?.vendor_type === 'lab' ? 'lab-tests' :
                 vendor?.vendor_type === 'pharma' ? 'medicines' : 
                 vendor?.vendor_type === 'chemist' ? 'medicines' : 'inventory';
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/bulk-import/template/${type}`, '_blank');
  };

  // Multiple report upload function
  const handleMultipleReportUpload = async (orderId, files) => {
    if (!files || files.length === 0) return;
    setUploadingMultipleReports(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      const res = await VendorAPI.post('/reports/upload-multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.reports?.length || 0} reports uploaded`);
      setMultipleReportFiles([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingMultipleReports(false);
    }
  };

  // Upload product photo
  const uploadProductPhoto = async (file) => {
    if (!file) return null;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/product-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        return data.url;
      }
      return null;
    } catch (err) {
      console.error('Photo upload error:', err);
      return null;
    }
  };

  // Initialize profile form when vendor data loads
  useEffect(() => {
    if (vendor) {
      setProfileForm({
        name: vendor.name || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        address: vendor.address || '',
        gst_number: vendor.gst_number || '',
        gst_percentage: vendor.gst_percentage ?? '',
        license_number: vendor.license_number || '',
        mr_name: vendor.mr_name || '',
        mr_phone: vendor.mr_phone || '',
        mr_email: vendor.mr_email || '',
        mr_territory: vendor.mr_territory || ''
      });
    }
  }, [vendor]);

  // Save profile changes
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...profileForm };
      if (payload.gst_percentage !== '' && payload.gst_percentage != null) {
        payload.gst_percentage = parseFloat(payload.gst_percentage);
      } else {
        delete payload.gst_percentage;
      }
      const res = await VendorAPI.put('/vendors/me', payload);
      setVendor(res.data);
      // Update localStorage so the data persists across sessions
      localStorage.setItem('vendor_user', JSON.stringify(res.data));
      setEditingProfile(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Invoice upload function
  const handleInvoiceUpload = async (orderId, file) => {
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/order-invoice/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        toast.success('Invoice uploaded successfully');
        fetchData();
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      toast.error('Failed to upload invoice');
    } finally {
      setUploadingInvoice(false);
    }
  };

  // My Doctors Functions
  const fetchMyDoctors = async () => {
    setDoctorsLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const [doctorsRes, pendingRes, availableRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/my-doctors`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/pending-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/available-doctors`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMyDoctors(doctorsRes.data || []);
      setPendingDoctorRequests(pendingRes.data || []);
      setAvailableDoctors(availableRes.data || []);
    } catch (err) {
      console.error('Failed to fetch my doctors', err);
      toast.error('Failed to load doctor connections');
    } finally {
      setDoctorsLoading(false);
    }
  };

  const requestDoctorConnection = async () => {
    if (!selectedDoctorId) {
      toast.error('Please select a doctor');
      return;
    }
    setRequestingDoctor(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/request-doctor`, 
        { target_id: selectedDoctorId, notes: requestDoctorNotes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Connection request sent for approval');
      setShowRequestDoctorDialog(false);
      setSelectedDoctorId('');
      setRequestDoctorNotes('');
      fetchMyDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequestingDoctor(false);
    }
  };

  // Credit settings functions
  const openCreditDialog = (mapping) => {
    setCreditEnabled(mapping.credit_enabled || false);
    setCreditLimit(mapping.credit_limit?.toString() || '');
    setCreditPeriodDays(mapping.credit_period_days?.toString() || '30');
    setCreditDialog({ open: true, mapping });
  };

  const saveCreditSettings = async () => {
    setSavingCredit(true);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/credit-settings/${creditDialog.mapping.id}`,
        { credit_enabled: creditEnabled, credit_limit: parseFloat(creditLimit) || 0, credit_period_days: parseInt(creditPeriodDays) || 30 },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Credit settings saved');
      setCreditDialog({ open: false, mapping: null });
      fetchMyDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credit settings');
    } finally { setSavingCredit(false); }
  };

  const fetchProductVisibility = async (doctor) => {
    setProductVisibilityLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/doctor/${doctor.doctor_id}/products`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setProductVisibilityDialog({
        open: true,
        doctor: doctor,
        products: res.data.products || []
      });
    } catch (err) {
      toast.error('Failed to load product visibility');
    } finally {
      setProductVisibilityLoading(false);
    }
  };

  const updateProductVisibility = async (productId, visible) => {
    const updatedProducts = productVisibilityDialog.products.map(p => 
      p.product_id === productId ? { ...p, visible } : p
    );
    setProductVisibilityDialog({ ...productVisibilityDialog, products: updatedProducts });
  };

  const updateProductCustomPrice = (productId, price) => {
    const updatedProducts = productVisibilityDialog.products.map(p => 
      p.product_id === productId ? { ...p, custom_price: price === '' ? null : parseFloat(price) } : p
    );
    setProductVisibilityDialog({ ...productVisibilityDialog, products: updatedProducts });
  };

  const saveProductVisibility = async () => {
    setSavingProductVisibility(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const visibility = {};
      const custom_prices = {};
      productVisibilityDialog.products.forEach(p => {
        visibility[p.product_id] = p.visible;
        if (p.custom_price != null && p.custom_price !== '' && !isNaN(p.custom_price) && p.custom_price > 0) {
          custom_prices[p.product_id] = parseFloat(p.custom_price);
        }
      });
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/doctor/${productVisibilityDialog.doctor.doctor_id}/products`,
        { product_visibility: visibility, custom_prices },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Product settings updated');
      setProductVisibilityDialog({ open: false, doctor: null, products: [] });
    } catch (err) {
      toast.error('Failed to update product settings');
    } finally {
      setSavingProductVisibility(false);
    }
  };

  // Stats - include marketing orders for marketing vendors
  const mktRevenue = marketingOrders.filter(o => ['confirmed', 'processing', 'delivered', 'completed'].includes(o.status)).reduce((sum, o) => sum + (o.price || 0), 0);
  const totalRevenue = vendor?.vendor_type === 'marketing' ? mktRevenue : orders.filter(o => ['confirmed', 'processing', 'shipped', 'delivered'].includes(o.status)).reduce((sum, o) => sum + (o.total || 0), 0);
  const pendingOrders = vendor?.vendor_type === 'marketing' ? marketingOrders.filter(o => o.status === 'confirmed').length : orders.filter(o => o.status === 'pending').length;
  const completedOrders = vendor?.vendor_type === 'marketing' ? marketingOrders.filter(o => o.status === 'delivered' || o.status === 'completed').length : orders.filter(o => o.status === 'delivered').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Header */}
      <header className={`bg-gradient-to-r ${getVendorColor(vendor?.vendor_type)} text-white shadow-lg sticky top-0 z-30`}>
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="w-9 h-9 md:w-10 md:h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              {getVendorIcon(vendor?.vendor_type)}
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-xl font-bold truncate">{vendor?.name}</h1>
              <p className="text-white/70 text-xs md:text-sm capitalize hidden sm:block">{vendor?.vendor_type} Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Wallet Balance */}
            <button onClick={() => setActiveTab('wallet')} className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-lg px-2.5 py-1.5 transition cursor-pointer" data-testid="vendor-header-wallet">
              <IndianRupee className="w-3.5 h-3.5" />
              <span className="text-sm font-bold">₹{vendorWalletBalance.toLocaleString('en-IN')}</span>
            </button>
            <DownloadAppButton 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/20 hidden md:flex"
            />
            <NotificationBell portalType="vendor" />
            <Badge className="bg-white/20 text-white text-[10px] md:text-xs hidden sm:inline-flex">{vendor?.status}</Badge>
            <Button variant="ghost" size="sm" onClick={handleLogoutClick} className="text-white hover:bg-white/20 p-2 md:px-3">
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6 pb-6">
        {/* Stats - Grid layout on all screens */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <Card>
            <CardContent className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-slate-500 truncate">Products</p>
                <p className="text-lg md:text-2xl font-bold">{products.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-slate-500 truncate">Pending</p>
                <p className="text-lg md:text-2xl font-bold">{pendingOrders}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-slate-500 truncate">Done</p>
                <p className="text-lg md:text-2xl font-bold">{completedOrders}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-slate-500 truncate">Revenue</p>
                <p className="text-lg md:text-2xl font-bold">₹{totalRevenue.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile-friendly Tab Navigation */}
        <MobileTabNav
          tabs={[
            { id: 'catalog', label: vendor?.vendor_type === 'lab' ? 'Lab Tests' : vendor?.vendor_type === 'pharma' ? 'Medicines' : vendor?.vendor_type === 'chemist' ? 'Products' : vendor?.vendor_type === 'marketing' ? 'Services' : 'Products', icon: Package },
            { id: 'orders', label: 'B2B Orders', icon: ShoppingCart, badge: pendingOrders > 0 ? pendingOrders : null, hidden: vendor?.vendor_type === 'marketing' },
            { id: 'marketing-orders', label: 'Service Orders', icon: ShoppingCart, hidden: vendor?.vendor_type !== 'marketing', onClick: fetchMarketingOrders },
            { id: 'patient-orders', label: 'Patient Orders', icon: User, onClick: fetchPatientPortalOrders, hidden: vendor?.vendor_type === 'marketing' },
            { id: 'chemist-b2b-orders', label: 'Chemist B2B Orders', icon: ShoppingCart, hidden: vendor?.vendor_type !== 'pharma', onClick: fetchChemistB2BOrders },
            { id: 'chemist-patient-orders', label: 'Chemist Patient Orders', icon: User, hidden: vendor?.vendor_type !== 'pharma', onClick: fetchChemistPatientOrders },
            { id: 'reports', label: 'Reports', icon: FileText, hidden: vendor?.vendor_type !== 'lab' },
            { id: 'book-for-doctor', label: 'Book For Doctor', icon: ClipboardList, hidden: vendor?.vendor_type !== 'lab' },
            { id: 'lis', label: 'Lab Management', icon: FileText, hidden: vendor?.vendor_type !== 'lab', onClick: () => navigate('/vendor/lis') },
            { id: 'earnings', label: 'Earnings', icon: IndianRupee, onClick: fetchEarningsData },
            { id: 'coupons', label: 'Coupons', icon: Tag, onClick: fetchCoupons },
            { id: 'mr-visits', label: 'MR Visits', icon: MapPin, onClick: fetchMrVisitsData, hidden: vendor?.vendor_type === 'chemist' },
            { id: 'my-doctors', label: 'My Doctors', icon: Users, onClick: fetchMyDoctors },
            { id: 'walkin-billing', label: 'Billing', icon: Receipt },
            { id: 'chemist-inventory', label: 'Inventory', icon: Package },
            { id: 'buy-pharma', label: 'Buy from Pharma', icon: ShoppingCart, hidden: vendor?.vendor_type !== 'chemist' },
            { id: 'my-vendors', label: vendor?.vendor_type === 'chemist' ? 'My Pharma' : 'My Chemists', icon: Building2, hidden: !['chemist','pharma'].includes(vendor?.vendor_type) },
            { id: 'wallet', label: 'Wallet', icon: CreditCard },
            { id: 'referral-ledger', label: 'Referral Ledger', icon: FileText, hidden: vendor?.vendor_type === 'marketing' },
            { id: 'expiry-alerts', label: 'Expiry Alerts', icon: AlertCircle, onClick: fetchExpiryAlerts, hidden: vendor?.vendor_type === 'lab' || vendor?.vendor_type === 'marketing' },
            { id: 'support', label: 'Support', icon: HelpCircle, onClick: fetchSupportTickets },
            { id: 'profile', label: 'My Profile', icon: UserCircle, onClick: fetchVendorProfile },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Contents using Tabs for compatibility */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Hidden TabsList - MobileTabNav controls the tabs */}
          <TabsList className="hidden">
            <TabsTrigger value="catalog">Catalog</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="patient-orders">Patient Orders</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="coupons">Coupons</TabsTrigger>
            <TabsTrigger value="mr-visits">MR Visits</TabsTrigger>
            <TabsTrigger value="my-doctors">My Doctors</TabsTrigger>
            <TabsTrigger value="wallet">Wallet</TabsTrigger>
            <TabsTrigger value="referral-ledger">Referral Ledger</TabsTrigger>
            <TabsTrigger value="expiry-alerts">Expiry</TabsTrigger>
            <TabsTrigger value="marketing-orders">Marketing Orders</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Your Catalog</CardTitle>
                  <CardDescription className="text-sm">Manage your products and pricing</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-btn" className="flex-1 sm:flex-none">
                    <Upload className="w-4 h-4 mr-2" /> Import
                  </Button>
                  <ProductDialog
                    vendorType={vendor?.vendor_type}
                    product={editingProduct}
                    onSave={() => { setEditingProduct(null); setShowAddDialog(false); fetchData(); }}
                    onClose={() => { setEditingProduct(null); setShowAddDialog(false); }}
                    open={showAddDialog || !!editingProduct}
                    setOpen={(v) => { if (!v) { setEditingProduct(null); setShowAddDialog(false); } }}
                    trigger={<Button size="sm" onClick={() => setShowAddDialog(true)} className="flex-1 sm:flex-none"><Plus className="w-4 h-4 mr-2" /> Add</Button>}
                    uploadProductPhoto={uploadProductPhoto}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search products by name..."
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="pl-10"
                    data-testid="catalog-search-input"
                  />
                </div>
                {selectedProducts.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="catalog-selection-bar">
                    <span className="font-medium text-blue-700 text-sm">{selectedProducts.length} item(s) selected</span>
                    <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={handleBulkDeleteProducts} data-testid="bulk-delete-products-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
                    </Button>
                  </div>
                )}
                {(() => {
                  const filteredProducts = products.filter(p => (p.name || '').toLowerCase().includes(catalogSearch.toLowerCase()));
                  if (filteredProducts.length === 0) {
                    return (
                      <div className="text-center py-12 text-slate-500">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>{catalogSearch ? 'No products match your search' : 'No items in your catalog yet'}</p>
                        {!catalogSearch && (
                          <Button variant="link" className="mt-2" onClick={() => setShowBulkImport(true)}>
                            Import from Excel/CSV
                          </Button>
                        )}
                      </div>
                    );
                  }
                  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.includes(p.id));
                  const toggleSelectAll = () => setSelectedProducts(allSelected ? [] : filteredProducts.map(p => p.id));
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          data-testid="select-all-products"
                        />
                        <label className="text-sm text-slate-600 cursor-pointer" onClick={toggleSelectAll}>Select All ({filteredProducts.length})</label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[1080px] overflow-y-auto pr-1" data-testid="catalog-scroll-container">
                        {filteredProducts.map((product) => (
                        <div key={product.id} className={`border rounded-lg p-4 relative ${!product.is_active ? 'opacity-60 bg-slate-50' : ''} ${selectedProducts.includes(product.id) ? 'ring-2 ring-blue-300' : ''}`}>
                          <input
                            type="checkbox"
                            className="absolute top-3 left-3 w-4 h-4 z-10"
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => toggleSelectProduct(product.id)}
                            data-testid={`select-product-${product.id}`}
                          />
                          {product.photo_url && (
                            <div
                              className="mb-3 h-32 bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setLightboxPhoto({ url: `${process.env.REACT_APP_BACKEND_URL}${product.photo_url}`, name: product.name })}
                            >
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${product.photo_url}`} alt={product.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex items-start justify-between mb-2 pl-6">
                            <h3 className="font-semibold text-slate-800">{product.name}</h3>
                            {!product.is_active && <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-slate-500 mb-3 line-clamp-2">{product.description || product.composition || product.category}</p>
                          <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                            <div><p className="text-xs text-slate-400">MRP</p><p className="font-medium text-slate-500 line-through">₹{product.mrp}</p></div>
                            <div><p className="text-xs text-slate-400">B2B</p><p className="font-bold text-green-600">₹{product.b2b_price}</p></div>
                            <div><p className="text-xs text-slate-400">Patient</p><p className="font-bold text-purple-600">₹{product.patient_mrp || product.mrp}</p></div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditingProduct(product)}>
                              <Edit2 className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteProduct(product.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="mt-3 pt-3 border-t">
                            <ReviewSection productId={product.id} vendorId={vendor?.id} productType={vendor?.vendor_type === 'marketing' ? 'marketing_service' : vendor?.vendor_type === 'lab' ? 'lab_test' : vendor?.vendor_type === 'inventory' ? 'inventory_product' : 'medicine'} canReply={true} token={localStorage.getItem('vendor_token')} />
                          </div>
                        </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Vendor Banner Ads */}
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Image className="w-4 h-4 text-indigo-600" /> My Banner Ads
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Title (optional)"
                      value={vBannerTitle}
                      onChange={(e) => setVBannerTitle(e.target.value)}
                      className="w-36 h-8 text-xs"
                    />
                    <input
                      type="file"
                      ref={vBannerFileRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVendorBanner(f); e.target.value = ''; }}
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={() => vBannerFileRef.current?.click()} disabled={uploadingVBanner} data-testid="upload-vendor-banner-btn">
                      {uploadingVBanner ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                      {vendorBanners.length > 0 ? 'Replace Banner' : 'Add Banner'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">
                  {vendorBanners.length > 0 
                    ? 'Your banner is displayed on linked doctors\' dashboards. Upload a new image to replace it.' 
                    : 'Upload a banner to display on your linked doctors\' dashboards as an auto-carousel.'} Recommended size: <span className="font-semibold text-slate-700">1200 x 300 px</span> (4:1 ratio, landscape). Max 5 MB.
                </p>
                {vendorBanners.length === 0 ? (
                  <p className="text-center text-slate-400 py-4 text-sm">No banner yet</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {vendorBanners.map(banner => (
                      <div key={banner.id} className="relative rounded-lg overflow-hidden border border-slate-200" data-testid={`vendor-banner-${banner.id}`}>
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`}
                          alt={banner.title || 'Banner'}
                          className="w-full h-28 object-cover"
                        />
                        <div className="p-2 bg-white flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 truncate flex-1">{banner.title || 'Untitled'}</p>
                          <Button variant="ghost" size="sm" className="text-red-500 h-6 w-6 p-0" onClick={() => deleteVendorBanner(banner.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="orders">
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Order #, clinic, patient..."
                        value={orderFilters.search}
                        onChange={(e) => setOrderFilters({ ...orderFilters, search: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Clinic</Label>
                    <Select value={orderFilters.clinic_id} onValueChange={(v) => setOrderFilters({ ...orderFilters, clinic_id: v })}>
                      <SelectTrigger><SelectValue placeholder="All Clinics" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clinics</SelectItem>
                        {clinics.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Status</Label>
                    <Select value={orderFilters.status} onValueChange={(v) => setOrderFilters({ ...orderFilters, status: v })}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={orderFilters.date_from} onChange={(e) => setOrderFilters({ ...orderFilters, date_from: e.target.value })} />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={orderFilters.date_to} onChange={(e) => setOrderFilters({ ...orderFilters, date_to: e.target.value })} />
                  </div>
                  {(vendor?.vendor_type === 'pharma') && (
                    <div className="w-36">
                      <Label className="text-xs">Buyer Type</Label>
                      <Select value={orderFilters.buyer_type || 'all'} onValueChange={(v) => setOrderFilters({ ...orderFilters, buyer_type: v })}>
                        <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Buyers</SelectItem>
                          <SelectItem value="doctor">Doctors</SelectItem>
                          <SelectItem value="chemist">Chemists</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="w-36">
                    <Label className="text-xs">Payment</Label>
                    <Select value={orderFilters.payment_method || 'all'} onValueChange={(v) => setOrderFilters({ ...orderFilters, payment_method: v })}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="wallet">Wallet</SelectItem>
                        <SelectItem value="credit">On Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Order Type</Label>
                    <Select value={orderFilters.order_type || 'all'} onValueChange={(v) => setOrderFilters({ ...orderFilters, order_type: v })}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="purchase">Purchase</SelectItem>
                        <SelectItem value="subscription">Subscription</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchFilteredOrders}><Filter className="w-4 h-4 mr-1" /> Filter</Button>
                </div>
              </CardContent>
            </Card>

            {/* Orders List */}
            <Card>
              <CardHeader>
                <CardTitle>Orders</CardTitle>
                <CardDescription>Manage incoming orders from clinics</CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-mono text-sm text-slate-500">{order.order_number}</p>
                            <p className="font-semibold">{order.clinic_name || order.buyer_vendor_name || 'Unknown'}</p>
                            {order.buyer_type && <Badge variant="outline" className={`text-[10px] mt-0.5 ${order.buyer_type === 'chemist' ? 'border-teal-300 text-teal-600' : 'border-blue-300 text-blue-600'}`}>{order.buyer_type === 'chemist' ? 'Chemist Order' : 'Doctor Order'}</Badge>}
                            {order.patient_name && <p className="text-sm text-slate-500">Patient: {order.patient_name} {order.patient_phone && `• ${order.patient_phone}`}</p>}
                            {order.patient_address && <p className="text-sm text-slate-400">Address: {order.patient_address}</p>}
                            {order.delivery_address && <p className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> {order.delivery_address}</p>}
                            {order.delivery_phone && <p className="text-sm text-slate-400 flex items-center gap-1"><Phone className="h-3 w-3" /> <a href={`tel:${order.delivery_phone}`} className="text-blue-600 hover:underline">{order.delivery_phone}</a></p>}
                            {order.delivery_location?.lat && (
                              <a href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> Open in Maps</a>
                            )}
                            {order.sample_collection_time && <p className="text-sm text-slate-400">Sample Collection: {new Date(order.sample_collection_time).toLocaleString()}</p>}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">₹{order.total?.toLocaleString()}</p>
                            {getStatusBadge(order.status)}
                            {order.payment_method === 'credit' && (
                              <Badge className={`mt-1 ${
                                order.credit_status === 'paid' ? 'bg-green-100 text-green-700' :
                                order.credit_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                <CreditCard className="w-3 h-3 mr-1" />
                                {order.credit_status === 'paid' ? 'Credit Paid' : order.credit_status === 'overdue' ? 'Credit Overdue' : 'On Credit'}
                              </Badge>
                            )}
                            {order.order_type === 'subscription' && (
                              <Badge className="mt-1 bg-purple-100 text-purple-700">
                                <RefreshCw className="w-3 h-3 mr-1" /> {order.subscription_period || 'Subscription'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 mb-3">
                          <p className="text-xs text-slate-500 mb-2">Items:</p>
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.item_name} x{item.quantity}</span>
                              <span>₹{item.total_price}</span>
                            </div>
                          ))}
                        </div>
                        {order.notes && <p className="text-sm text-slate-500 mb-3"><span className="font-medium">Notes:</span> {order.notes}</p>}
                        
                        {/* Payment Breakdown for B2B Orders */}
                        <div className="mt-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                          <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                            <IndianRupee className="w-3 h-3" /> PAYMENT BREAKDOWN
                          </p>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-slate-600">MRP</span>
                              <span className="font-medium">₹{(order.total_mrp || order.mrp || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">B2B Price</span>
                              <span className="font-medium text-teal-600">₹{(order.subtotal || order.total || 0).toLocaleString()}</span>
                            </div>
                            {(order.platform_commission > 0 || order.admin_commission > 0) && (
                              <div className="flex justify-between text-red-600">
                                <span>Platform Fee</span>
                                <span>-₹{(order.platform_commission || order.admin_commission || 0).toLocaleString()}</span>
                              </div>
                            )}
                            {(order.tax || order.gst_amount || 0) > 0 && (
                              <div className="flex justify-between text-slate-500">
                                <span>GST</span>
                                <span>-₹{(order.tax || order.gst_amount || 0).toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-slate-300 font-bold text-emerald-700">
                              <span>Net Vendor Payout</span>
                              <span>₹{(order.vendor_payout || (order.subtotal || order.total || 0) - (order.platform_commission || 0)).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                          <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString()}</p>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => shareOrderWhatsApp(order)} className="text-xs">
                              <MessageCircle className="w-3 h-3 mr-1" /> Share
                            </Button>
                            {order.payment_method === 'credit' && order.credit_status !== 'paid' && (
                              <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                                onClick={() => setMarkPaidOrder(order)} data-testid={`mark-paid-btn-${order.id}`}>
                                <CreditCard className="w-3 h-3 mr-1" /> Mark as Paid
                              </Button>
                            )}
                            {vendor?.vendor_type === 'lab' && order.status === 'confirmed' && (
                              <Button size="sm" variant="outline" onClick={() => { setReportUpload({ ...reportUpload, order_id: order.id, patient_name: order.patient_name || '', patient_phone: order.patient_phone || '' }); setShowUploadReport(true); }} className="text-xs">
                                <Upload className="w-3 h-3 mr-1" /> Upload
                              </Button>
                            )}
                            {getNextStatus(order.status) && (
                              <Button size="sm" onClick={() => handleOrderAction(order.id, getNextStatus(order.status))} className="text-xs">
                                {getNextStatus(order.status)}
                              </Button>
                            )}
                            {order.status === 'pending' && (
                              <Button size="sm" variant="destructive" onClick={() => handleOrderAction(order.id, 'cancelled', 'Cancelled by vendor')} className="text-xs">Cancel</Button>
                            )}
                          </div>
                        </div>
                        <InvoiceUploader orderId={order.id} orderType="b2b" canUpload={true} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patient Portal Orders Tab */}
          <TabsContent value="patient-orders">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-500" /> Patient Portal Orders
                    </CardTitle>
                    <CardDescription>Orders from patients who bought via the Patient Portal marketplace</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchPatientPortalOrders} disabled={patientPortalOrdersLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${patientPortalOrdersLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Search Patient/Order</Label>
                    <Input
                      placeholder="Name, Phone, Order #"
                      value={patientPortalOrderFilters.search}
                      onChange={(e) => setPatientPortalOrderFilters({ ...patientPortalOrderFilters, search: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                    <Select
                      value={patientPortalOrderFilters.status}
                      onValueChange={(v) => setPatientPortalOrderFilters({ ...patientPortalOrderFilters, status: v })}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                    <Input
                      type="date"
                      value={patientPortalOrderFilters.date_from}
                      onChange={(e) => setPatientPortalOrderFilters({ ...patientPortalOrderFilters, date_from: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                    <Input
                      type="date"
                      value={patientPortalOrderFilters.date_to}
                      onChange={(e) => setPatientPortalOrderFilters({ ...patientPortalOrderFilters, date_to: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button size="sm" className="h-9" onClick={fetchPatientPortalOrders}>
                      <Search className="w-4 h-4 mr-1" /> Search
                    </Button>
                    {(patientPortalOrderFilters.search || patientPortalOrderFilters.date_from || patientPortalOrderFilters.date_to || patientPortalOrderFilters.status !== 'all' || patientOrderDoctorSearch) && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-9"
                        onClick={() => {
                          setPatientPortalOrderFilters({ status: 'all', search: '', date_from: '', date_to: '' });
                          setPatientOrderDoctorSearch('');
                          setTimeout(fetchPatientPortalOrders, 100);
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-3">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs text-slate-500 mb-1 block">Referring Doctor</Label>
                    <Input
                      placeholder="Search by doctor name..."
                      value={patientOrderDoctorSearch}
                      onChange={(e) => setPatientOrderDoctorSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchPatientPortalOrders()}
                      className="h-9"
                      data-testid="patient-order-doctor-search"
                    />
                  </div>
                </div>
                {patientOrderFilteredSummary && patientOrderFilteredSummary.count > 0 && (
                  <div className="mt-3 p-2.5 bg-violet-50 border border-violet-200 rounded-lg flex items-center gap-4 text-xs">
                    <span className="font-medium text-violet-700">{patientOrderFilteredSummary.count} orders</span>
                    <span className="text-slate-500">|</span>
                    <span>Subtotal: <strong className="text-violet-700">₹{(patientOrderFilteredSummary.total_subtotal || 0).toLocaleString()}</strong></span>
                    <span className="text-slate-500">|</span>
                    <span>Platform Fee: <strong className="text-red-600">₹{(patientOrderFilteredSummary.total_admin_commission || 0).toLocaleString()}</strong></span>
                    {(patientOrderFilteredSummary.total_gst || 0) > 0 && (
                      <>
                        <span className="text-slate-500">|</span>
                        <span>GST: <strong className="text-slate-600">₹{patientOrderFilteredSummary.total_gst.toLocaleString()}</strong></span>
                      </>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {patientPortalOrdersLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-500" />
                    <p className="text-slate-500 mt-2">Loading patient orders...</p>
                  </div>
                ) : patientPortalOrders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-purple-200" />
                    <p className="font-medium">No patient orders yet</p>
                    <p className="text-sm mt-1">When patients order your products from the Patient Portal, they'll appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {patientPortalOrders.map((order) => (
                      <div key={order.id} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          {/* Order Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className="font-bold text-slate-800">{order.order_number}</span>
                              <Badge variant={
                                order.status === 'delivered' ? 'default' :
                                order.status === 'cancelled' ? 'destructive' :
                                order.status === 'pending' ? 'secondary' :
                                'outline'
                              }>
                                {order.status?.toUpperCase()}
                              </Badge>
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                ₹{order.vendor_subtotal?.toLocaleString()}
                              </Badge>
                            </div>
                            
                            {/* Patient Details */}
                            <div className="bg-purple-50 rounded-lg p-3 mb-3">
                              <p className="text-xs text-purple-600 font-medium mb-1">PATIENT DETAILS</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-purple-500" />
                                  <span className="font-medium">{order.patient_name || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-purple-500" />
                                  <span>{order.patient_phone || 'N/A'}</span>
                                </div>
                                {order.doctor_name && (
                                  <div className="flex items-center gap-2 sm:col-span-2">
                                    <Stethoscope className="w-4 h-4 text-teal-500" />
                                    <span className="text-teal-700 font-medium">Ref. Doctor: {order.doctor_name}</span>
                                  </div>
                                )}
                                {order.patient_email && (
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-purple-500" />
                                    <span>{order.patient_email}</span>
                                  </div>
                                )}
                                {order.delivery_address && (
                                  <div className="flex items-start gap-2 sm:col-span-2">
                                    <MapPin className="w-4 h-4 text-purple-500 mt-0.5" />
                                    <span className="text-slate-600">{order.delivery_address}</span>
                                  </div>
                                )}
                                {order.delivery_phone && (
                                  <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-green-500" />
                                    <a href={`tel:${order.delivery_phone}`} className="text-blue-600 hover:underline">{order.delivery_phone}</a>
                                  </div>
                                )}
                                {order.delivery_location?.lat && (
                                  <div className="sm:col-span-2">
                                    <a
                                      href={`https://www.google.com/maps?q=${order.delivery_location.lat},${order.delivery_location.lng}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                      data-testid={`map-link-${order.order_number}`}
                                    >
                                      <MapPin className="h-3 w-3" /> Open in Google Maps
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Items */}
                            <div className="space-y-1">
                              <p className="text-xs text-slate-500 font-medium">ITEMS ({order.vendor_items?.length || 0})</p>
                              {order.vendor_items?.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center text-sm bg-slate-50 px-2 py-1 rounded">
                                  <span>{item.product_name}</span>
                                  <span className="text-slate-600">
                                    {item.quantity} × ₹{item.unit_price} = <strong>₹{item.line_total}</strong>
                                  </span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Prescribing Doctor */}
                            {order.prescribing_doctor_name && (
                              <p className="text-xs text-slate-400 mt-2">
                                Prescribed by: Dr. {order.prescribing_doctor_name}
                              </p>
                            )}
                            
                            {/* Order Date */}
                            <p className="text-xs text-slate-400 mt-1">
                              Ordered: {new Date(order.created_at).toLocaleString()}
                            </p>
                            
                            {/* Payment Breakdown for Patient Portal Orders */}
                            <div className="mt-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                                <IndianRupee className="w-3 h-3" /> PAYMENT BREAKDOWN
                              </p>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Patient MRP (Subtotal)</span>
                                  <span className="font-medium">₹{order.subtotal?.toLocaleString() || order.vendor_subtotal?.toLocaleString() || 0}</span>
                                </div>
                                {order.admin_commission > 0 && (
                                  <div className="flex justify-between text-red-600">
                                    <span>Platform Fee</span>
                                    <span>-₹{order.admin_commission?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                {(order.gst_amount || order.tax || 0) > 0 && (
                                  <div className="flex justify-between text-slate-500">
                                    <span>GST</span>
                                    <span>-₹{(order.gst_amount || order.tax || 0).toLocaleString()}</span>
                                  </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-slate-300 font-bold text-emerald-700">
                                  <span>Net Vendor Payout</span>
                                  <span>₹{order.vendor_payout?.toLocaleString() || order.vendor_subtotal?.toLocaleString() || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex flex-col gap-2 min-w-[140px]">
                            <p className="text-xs text-slate-500 font-medium">CHANGE STATUS</p>
                            <Select
                              value={order.status}
                              onValueChange={(status) => updatePatientPortalOrderStatus(order.id, status)}
                            >
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {/* Quick Actions */}
                            {order.patient_phone && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(`https://wa.me/${order.patient_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                              </Button>
                            )}
                            
                            {vendor?.vendor_type === 'lab' && order.status === 'confirmed' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setReportUpload({ 
                                    order_id: order.id, 
                                    patient_name: order.patient_name || '', 
                                    patient_phone: order.patient_phone || '',
                                    report_title: '',
                                    file: null
                                  });
                                  setShowUploadReport(true);
                                }}
                              >
                                <Upload className="w-4 h-4 mr-1" /> Upload Report
                              </Button>
                            )}
                          </div>
                        </div>
                        <InvoiceUploader orderId={order.id} orderType="patient" canUpload={true} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab (Lab Only) */}
          {vendor?.vendor_type === 'lab' && (
            <TabsContent value="reports">
              <Card className="mb-4">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Lab Reports</CardTitle>
                    <CardDescription>Upload and manage patient reports</CardDescription>
                  </div>
                  <Button onClick={() => { fetchPatientPortalOrders(); setShowUploadReport(true); }}>
                    <Upload className="w-4 h-4 mr-2" /> Upload Report
                  </Button>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No reports uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reports.map((report) => (
                        <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium">{report.report_title}</p>
                              <p className="text-sm text-slate-500">Patient: {report.patient_name}</p>
                              <p className="text-xs text-slate-400">{new Date(report.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteReport(report.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Patient Search for Easy Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Upload by Patient</CardTitle>
                  <CardDescription>Search patients from your orders to upload reports quickly</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by patient name, clinic, or phone..."
                        className="pl-10"
                        onChange={(e) => {
                          const search = e.target.value.toLowerCase();
                          if (!search) {
                            fetchData();
                          } else {
                            VendorAPI.get(`/reports/search-patients?search=${encodeURIComponent(search)}`).then(res => {
                              setPatients(res.data.patients || []);
                            }).catch(() => {});
                          }
                        }}
                      />
                    </div>
                    <Select value="all" onValueChange={(v) => {
                      if (v !== 'all') {
                        VendorAPI.get(`/reports/search-patients?clinic_id=${v}`).then(res => {
                          setPatients(res.data.patients || []);
                        }).catch(() => {});
                      } else {
                        fetchData();
                      }
                    }}>
                      <SelectTrigger className="w-48"><SelectValue placeholder="Filter by Clinic" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clinics</SelectItem>
                        {clinics.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    {patients.slice(0, 10).map((patient, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{patient.patient_name}</p>
                          <p className="text-sm text-slate-500">{patient.clinic_name} • {patient.patient_phone}</p>
                          <p className="text-xs text-slate-400">Tests: {patient.recent_orders?.map(o => o.items?.join(', ')).join(', ')}</p>
                        </div>
                        <Button size="sm" onClick={() => {
                          const recentOrder = patient.recent_orders?.[0];
                          setReportUpload({
                            order_id: recentOrder?.order_id || '',
                            patient_id: patient.patient_id || '',
                            patient_name: patient.patient_name,
                            report_title: '',
                            file: null
                          });
                          setShowUploadReport(true);
                        }}>
                          <Upload className="w-4 h-4 mr-1" /> Upload
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Book For Doctor Tab (Lab vendors only) */}
          {vendor?.vendor_type === 'lab' && (
            <TabsContent value="book-for-doctor">
              <VendorBookForDoctorTab catalog={products.filter(p => p.is_active !== false)} />
            </TabsContent>
          )}

          {/* Earnings Tab */}
          {/* Chemist B2B Orders Tab (Pharma only) */}
          {vendor?.vendor_type === 'pharma' && (
            <TabsContent value="chemist-b2b-orders">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-teal-600" /> B2B Orders from Chemists</CardTitle>
                  <CardDescription>Orders placed by linked chemist vendors</CardDescription>
                </CardHeader>
                <CardContent>
                  {chemistB2BOrders.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">No B2B orders from chemists yet</p>
                  ) : (
                    <div className="space-y-3">
                      {chemistB2BOrders.map(order => (
                        <div key={order.id || order.order_number} className="border rounded-lg p-3 hover:bg-slate-50" data-testid={`chemist-b2b-order-${order.id}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-mono text-xs text-slate-500">{order.order_number}</p>
                              <p className="font-medium text-sm">{order.clinic_name || order.buyer_vendor_name || 'Chemist'}</p>
                              <Badge variant="outline" className="text-[10px] border-teal-300 text-teal-600 mt-0.5">Chemist Order</Badge>
                              <p className="text-xs text-slate-400 mt-1">{new Date(order.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-emerald-600">₹{order.total?.toLocaleString()}</p>
                              <Badge className={`text-xs capitalize ${order.status === 'delivered' ? 'bg-green-100 text-green-700' : order.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{order.status}</Badge>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-500">
                            {order.items?.map((item, i) => <span key={item.item_name || i} className="mr-3">{item.item_name} x{item.quantity}</span>)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Chemist Patient Orders Tab (Pharma only) */}
          {vendor?.vendor_type === 'pharma' && (
            <TabsContent value="chemist-patient-orders">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><User className="w-5 h-5 text-teal-600" /> Patient Orders via Chemists</CardTitle>
                  <CardDescription>Patient orders routed through linked chemist vendors</CardDescription>
                </CardHeader>
                <CardContent>
                  {chemistPatientOrders.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">No patient orders from chemists yet</p>
                  ) : (
                    <div className="space-y-3">
                      {chemistPatientOrders.map(order => (
                        <div key={order.id} className="border rounded-lg p-3 hover:bg-slate-50" data-testid={`chemist-patient-order-${order.id}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-mono text-xs text-slate-500">{order.order_number || order.id}</p>
                              <p className="font-medium text-sm">{order.patient_name || 'Patient'}</p>
                              <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-emerald-600">₹{order.total?.toLocaleString()}</p>
                              <Badge className={`text-xs capitalize ${order.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{order.status}</Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="earnings">
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">

                {/* Earnings Table - Full Width */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" /> Earnings Breakdown
                    </CardTitle>
                    <CardDescription>Complete breakdown of all orders with referral charges details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Filters Section */}
                    <div className="bg-slate-50 p-4 rounded-lg mb-4">
                      <div className="flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-xs text-slate-500">Search</Label>
                          <Input
                            placeholder="Order #, patient, doctor..."
                            value={earningsSearch}
                            onChange={(e) => setEarningsSearch(e.target.value)}
                            className="bg-white"
                          />
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">Order Type</Label>
                          <Select value={earningsOrderTypeFilter || 'all'} onValueChange={(v) => setEarningsOrderTypeFilter(v)}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Orders</SelectItem>
                              <SelectItem value="b2b">B2B Orders</SelectItem>
                              <SelectItem value="patient">Patient Orders</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">Status</Label>
                          <Select value={earningsStatusFilter} onValueChange={setEarningsStatusFilter}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Status</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">Payout</Label>
                          <Select value={earningsPayoutFilter} onValueChange={setEarningsPayoutFilter}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="settled">Settled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">Payment</Label>
                          <Select value={earningsPaymentFilter || 'all'} onValueChange={(v) => setEarningsPaymentFilter(v)}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="wallet">Wallet</SelectItem>
                              <SelectItem value="credit">Credit</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">Type</Label>
                          <Select value={earningsOrderTypeFilter || 'all'} onValueChange={(v) => setEarningsOrderTypeFilter(v)}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="purchase">Purchase</SelectItem>
                              <SelectItem value="subscription">Subscription</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">From</Label>
                          <Input type="date" value={earningsDateFrom} onChange={(e) => setEarningsDateFrom(e.target.value)} className="bg-white" />
                        </div>
                        <div className="w-[130px]">
                          <Label className="text-xs text-slate-500">To</Label>
                          <Input type="date" value={earningsDateTo} onChange={(e) => setEarningsDateTo(e.target.value)} className="bg-white" />
                        </div>
                        <Button onClick={applyEarningsFilters} size="sm" className="bg-teal-600 hover:bg-teal-700">
                          <Search className="w-4 h-4 mr-1" /> Filter
                        </Button>
                        <Button variant="outline" size="sm" onClick={resetEarningsFilters}>
                          <RefreshCw className="w-4 h-4 mr-1" /> Reset
                        </Button>
                      </div>
                    </div>

                    {/* Totals Row - Using API summary */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg mb-4 border border-emerald-200">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                        <div>
                          <p className="text-xs text-slate-500">Total Orders</p>
                          <p className="text-lg font-bold text-slate-800">{earningsOrders.length}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Total MRP</p>
                          <p className="text-lg font-bold text-slate-800">₹{(earningsSummary?.total_mrp || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Platform Fee</p>
                          <p className="text-lg font-bold text-red-600">₹{(earningsSummary?.total_admin_commission || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Net Payout</p>
                          <p className="text-lg font-bold text-emerald-700">₹{(earningsSummary?.total_vendor_payout || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Received</p>
                          <p className="text-lg font-bold text-green-700">₹{(earningsSummary?.total_received || 0).toLocaleString()}</p>
                        </div>
                        {earningsSummary?.pending_payout_requests > 0 && (
                          <div className="bg-amber-50 rounded-lg p-1">
                            <p className="text-xs text-amber-600 font-semibold">Pending Requests</p>
                            <p className="text-lg font-bold text-amber-700">₹{(earningsSummary?.pending_payout_requests || 0).toLocaleString()}</p>
                          </div>
                        )}
                        <div className="bg-white/50 rounded-lg p-1">
                          <p className="text-xs text-emerald-600 font-semibold">Available</p>
                          <p className="text-lg font-bold text-emerald-700">₹{(earningsSummary?.available_for_payout || 0).toLocaleString()}</p>
                        </div>
                        {earningsSummary?.total_credit_pending > 0 && (
                          <div className="bg-blue-50 rounded-lg p-1">
                            <p className="text-xs text-blue-600 font-semibold">Credit Pending</p>
                            <p className="text-lg font-bold text-blue-700">₹{(earningsSummary?.total_credit_pending || 0).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {earningsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                      </div>
                    ) : earningsOrders.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p>No orders found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-slate-100">
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">S.No</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Order</th>
                              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-600">Products</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">MRP</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">Platform Fee</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">GST</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">Net Payout</th>
                              <th className="text-right py-3 px-3 text-xs font-semibold text-slate-600">Received</th>
                              <th className="text-center py-3 px-3 text-xs font-semibold text-slate-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {earningsOrders.map((order, index) => (
                              <tr key={order.id} className="border-b hover:bg-slate-50">
                                <td className="py-3 px-3 text-sm text-slate-500">{index + 1}</td>
                                <td className="py-3 px-3">
                                  <div className="flex items-center gap-2">
                                    <Badge className={order.order_type === 'patient' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                                      {order.order_type === 'patient' ? 'P' : 'B2B'}
                                    </Badge>
                                    <div>
                                      <p className="font-mono text-xs text-slate-800">{order.order_number}</p>
                                      <p className="text-xs text-slate-500">{order.doctor_name || order.clinic_name}</p>
                                      {order.patient_name && <p className="text-xs text-slate-400">Patient: {order.patient_name}</p>}
                                      <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleDateString('en-IN')}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-3">
                                  {order.items?.slice(0, 2).map((item, idx) => (
                                    <p key={idx} className="text-xs text-slate-600">{item.item_name || item.product_name || item.name || 'Item'} x{item.quantity}</p>
                                  ))}
                                  {order.items?.length > 2 && <p className="text-xs text-slate-400">+{order.items.length - 2} more</p>}
                                  {(!order.items || order.items.length === 0) && <p className="text-xs text-slate-400">-</p>}
                                </td>
                                <td className="py-3 px-3 text-right font-medium text-sm">₹{(order.mrp || order.total_mrp || order.subtotal || 0).toLocaleString()}</td>
                                <td className="py-3 px-3 text-right text-sm text-red-600">₹{(order.platform_commission || order.admin_commission || 0).toLocaleString()}</td>
                                <td className="py-3 px-3 text-right text-sm text-slate-500">{(order.tax || 0) > 0 ? `₹${order.tax.toLocaleString()}` : '-'}</td>
                                <td className="py-3 px-3 text-right font-bold text-sm text-emerald-700">₹{(order.vendor_payout || 0).toLocaleString()}</td>
                                <td className="py-3 px-3 text-right text-sm">
                                  {order.payout_status === 'settled' ? (
                                    <span className="text-green-700 font-medium">₹{(order.vendor_payout || 0).toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <div className="flex flex-col gap-1">
                                    <Badge className={
                                      order.status === 'completed' || order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                      order.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                      'bg-slate-100 text-slate-700'
                                    }>{order.status}</Badge>
                                    <Badge className={order.payout_status === 'settled' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                      {order.payout_status || 'pending'}
                                    </Badge>
                                    {order.payment_method === 'credit' && (
                                      <Badge className={
                                        order.credit_status === 'paid' ? 'bg-green-100 text-green-700' :
                                        order.credit_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                        'bg-blue-100 text-blue-700'
                                      }>
                                        <CreditCard className="w-3 h-3 mr-0.5" />
                                        {order.credit_status === 'paid' ? 'Credit Paid' : order.credit_status === 'overdue' ? 'Overdue' : 'Credit'}
                                      </Badge>
                                    )}
                                    {order.order_type === 'subscription' && (
                                      <Badge className="bg-purple-100 text-purple-700">
                                        <RefreshCw className="w-3 h-3 mr-0.5" /> {order.subscription_period || 'Sub'}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Tag className="w-5 h-5" /> Coupon Management
                    </CardTitle>
                    <CardDescription>Create and manage discount coupons for clinics</CardDescription>
                  </div>
                  <Button onClick={() => { setEditingCoupon(null); setCouponForm({ code: '', description: '', discount_type: 'percentage', discount_value: 0, min_order_value: 0, max_uses: 0, valid_until: '', is_active: true, applicable_tiers: [] }); setShowCouponDialog(true); }} data-testid="add-coupon-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Coupon
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {coupons.length === 0 ? (
                  <div className="text-center py-8">
                    <Tag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No coupons created yet</p>
                    <Button variant="link" onClick={() => setShowCouponDialog(true)}>Create your first coupon</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {coupons.map((coupon) => (
                      <div key={coupon.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`coupon-${coupon.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Tag className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-lg">{coupon.code}</span>
                              {coupon.is_active ? (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              ) : (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `₹${coupon.discount_value} off`}
                              {coupon.min_order_value > 0 && ` (Min: ₹${coupon.min_order_value})`}
                            </p>
                            {coupon.description && <p className="text-xs text-slate-400">{coupon.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-4">
                            <p className="text-sm text-slate-500">Uses: {coupon.current_uses || 0}{coupon.max_uses > 0 ? `/${coupon.max_uses}` : ''}</p>
                            {coupon.valid_until && <p className="text-xs text-slate-400">Until: {new Date(coupon.valid_until).toLocaleDateString()}</p>}
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => openEditCoupon(coupon)} data-testid={`edit-coupon-${coupon.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteCoupon(coupon.id)} data-testid={`delete-coupon-${coupon.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MR Visits Tab - Real-time Monitoring */}
          <TabsContent value="mr-visits">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Today's Visits</p>
                      <p className="text-2xl font-bold">{mrVisitsStats.today}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Verified Today</p>
                      <p className="text-2xl font-bold text-green-600">{mrVisitsStats.verified_today}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">This Month</p>
                      <p className="text-2xl font-bold">{mrVisitsStats.this_month}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Navigation className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total All Time</p>
                      <p className="text-2xl font-bold">{mrVisitsStats.total}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Live Visits Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        Live MR Check-Ins
                      </CardTitle>
                      <CardDescription>Real-time monitoring of MR visits at clinics</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={mrAutoRefresh} 
                          onCheckedChange={setMrAutoRefresh}
                          id="auto-refresh"
                        />
                        <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={fetchMrLiveVisits}
                        disabled={mrVisitsLoading}
                      >
                        {mrVisitsLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Active Visits */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      Active Visits ({mrLiveVisits.active_visits?.length || 0})
                    </h4>
                    {mrLiveVisits.active_visits?.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50 rounded-lg">
                        <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-slate-500">No active MR visits right now</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {mrLiveVisits.active_visits?.map((visit) => (
                          <div 
                            key={visit.id} 
                            className="p-4 bg-green-50 border border-green-200 rounded-lg"
                            data-testid={`active-visit-${visit.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="w-4 h-4 text-green-600" />
                                  <span className="font-semibold text-slate-800">{visit.mr_name}</span>
                                  <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div>
                                    <span className="text-slate-500">Clinic:</span>
                                    <p className="font-medium">{visit.clinic_name}</p>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Phone:</span>
                                    <p className="font-medium">{visit.phone}</p>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Check-In:</span>
                                    <p className="font-medium">{new Date(visit.check_in_time).toLocaleTimeString()}</p>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Purpose:</span>
                                    <p className="font-medium capitalize">{visit.purpose?.replace('_', ' ') || '-'}</p>
                                  </div>
                                </div>
                                {/* Location Info */}
                                <div className="mt-3 flex items-center gap-2 text-sm">
                                  <MapPin className={`w-4 h-4 ${visit.location_verified ? 'text-green-600' : 'text-yellow-600'}`} />
                                  <span className={visit.location_verified ? 'text-green-700' : 'text-yellow-700'}>
                                    {visit.location_verified ? 'Location Verified' : 'Location Not Verified'}
                                    {visit.distance_from_clinic !== null && ` (${visit.distance_from_clinic}m from clinic)`}
                                  </span>
                                </div>
                                {visit.location_address && (
                                  <p className="text-xs text-slate-500 mt-1 truncate max-w-md">
                                    {visit.location_address}
                                  </p>
                                )}
                              </div>
                              {visit.latitude && visit.longitude && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`, '_blank')}
                                >
                                  <ExternalLink className="w-4 h-4 mr-1" /> Map
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent Completed Visits */}
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      Recently Completed ({mrLiveVisits.recent_completed?.length || 0})
                    </h4>
                    {mrLiveVisits.recent_completed?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No completed visits today</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50">
                              <th className="text-left p-3 font-medium">MR Name</th>
                              <th className="text-left p-3 font-medium">Clinic</th>
                              <th className="text-left p-3 font-medium">Phone</th>
                              <th className="text-left p-3 font-medium">Check-In</th>
                              <th className="text-left p-3 font-medium">Check-Out</th>
                              <th className="text-left p-3 font-medium">Location</th>
                              <th className="text-left p-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mrLiveVisits.recent_completed?.map((visit) => (
                              <tr key={visit.id} className="border-b hover:bg-slate-50" data-testid={`completed-visit-${visit.id}`}>
                                <td className="p-3 font-medium">{visit.mr_name}</td>
                                <td className="p-3">{visit.clinic_name}</td>
                                <td className="p-3">{visit.phone}</td>
                                <td className="p-3">{new Date(visit.check_in_time).toLocaleTimeString()}</td>
                                <td className="p-3">{visit.check_out_time ? new Date(visit.check_out_time).toLocaleTimeString() : '-'}</td>
                                <td className="p-3">
                                  {visit.location_verified ? (
                                    <Badge className="bg-green-100 text-green-700">Verified</Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-700">Unverified</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {visit.latitude && visit.longitude && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => window.open(`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`, '_blank')}
                                    >
                                      <MapPin className="w-4 h-4" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Clinics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-purple-600" /> Top Clinics Visited (This Month)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {mrVisitsStats.top_clinics?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No data yet</p>
                    ) : (
                      <div className="space-y-2">
                        {mrVisitsStats.top_clinics?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="font-medium text-sm">{item.clinic}</span>
                            <Badge variant="secondary">{item.visits} visits</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top MRs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" /> Top MRs (This Month)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {mrVisitsStats.top_mrs?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No data yet</p>
                    ) : (
                      <div className="space-y-2">
                        {mrVisitsStats.top_mrs?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <div>
                              <span className="font-medium text-sm">{item.mr_name}</span>
                              {item.phone && <span className="text-xs text-slate-500 ml-2">{item.phone}</span>}
                            </div>
                            <Badge variant="secondary">{item.visits} visits</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>


          {/* Marketing Orders Tab */}
          <TabsContent value="marketing-orders">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Service Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {marketingOrders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No marketing service orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {marketingOrders.map(order => {
                      const createdAt = order.created_at ? new Date(order.created_at) : null;
                      const expiresAt = order.expires_at ? new Date(order.expires_at) : null;
                      const fmtD = (d) => d && !isNaN(d) ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                      return (
                        <div key={order.id} className="p-4 bg-slate-50 rounded-xl border" data-testid={`mkt-order-${order.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <h4 className="font-semibold text-slate-800">{order.service_name}</h4>
                                <Badge variant={order.status === 'confirmed' || order.status === 'completed' ? 'default' : order.status === 'cancelled' ? 'destructive' : 'secondary'} className="text-[10px]">
                                  {order.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-500 mb-2">{order.order_number} | {order.clinic_name}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                                <span>{order.subscription_period !== 'one_time' ? `${order.subscription_period?.replace('_', ' ')} subscription` : 'One-time'}</span>
                                <span>Ordered: {fmtD(createdAt)}</span>
                                {expiresAt && <span>Expires: {fmtD(expiresAt)}</span>}
                              </div>
                              {order.notes && <p className="text-xs text-slate-500 mt-2 italic">Note: {order.notes}</p>}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right space-y-0.5">
                                <p className="text-lg font-bold text-slate-900">₹{order.price?.toLocaleString()}</p>
                                <p className="text-[10px] text-slate-400">Commission: ₹{order.admin_commission?.toLocaleString() || 0}</p>
                                <p className="text-[10px] text-emerald-600 font-medium">Net: ₹{order.net_vendor_payout?.toLocaleString() || 0}</p>
                              </div>
                              <Select defaultValue={order.status} onValueChange={async (s) => {
                                try {
                                  await VendorAPI.put(`/marketing-services/vendor-orders/${order.id}/status?status=${s}`);
                                  toast.success(`Order ${s}`);
                                  fetchMarketingOrders();
                                } catch(e) { toast.error('Update failed'); }
                              }}>
                                <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="processing">Processing</SelectItem>
                                  <SelectItem value="delivered">Delivered</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <InvoiceUploader orderId={order.id} orderType="marketing" canUpload={true} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <div className="space-y-6">
              {/* Quick Contact Info - Dynamic from Admin */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" /> Support & Help
                  </CardTitle>
                  <CardDescription>Contact 1Clin Support for assistance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-4">Quick Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <Mail className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="font-medium">{platformSupport?.support_email || 'Not configured'}</p>
                        </div>
                        {platformSupport?.support_email && <Button size="sm" variant="outline" onClick={() => window.open(`mailto:${platformSupport.support_email}`)}>Email</Button>}
                      </div>
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <Phone className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="text-xs text-slate-500">Phone</p>
                          <p className="font-medium">{platformSupport?.support_phone || 'Not configured'}</p>
                        </div>
                        {platformSupport?.support_phone && <Button size="sm" variant="outline" onClick={() => window.open(`tel:${platformSupport.support_phone.replace(/\D/g, '')}`)}>Call</Button>}
                      </div>
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-xs text-slate-500">WhatsApp</p>
                          <p className="font-medium">{platformSupport?.whatsapp_number || 'Not configured'}</p>
                        </div>
                        {platformSupport?.whatsapp_number && <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/${platformSupport.whatsapp_number.replace(/\D/g, '')}`)}>Chat</Button>}
                      </div>
                    </div>
                    {(platformSupport?.office_address || platformSupport?.business_hours) && (
                      <div className="mt-4 pt-4 border-t border-purple-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {platformSupport?.business_hours && (
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-purple-600 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Business Hours</p>
                              <p className="font-medium">{platformSupport.business_hours}</p>
                            </div>
                          </div>
                        )}
                        {platformSupport?.office_address && (
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-purple-600 mt-0.5" />
                            <div>
                              <p className="text-xs text-slate-500">Office Address</p>
                              <p className="font-medium text-sm">{platformSupport.office_address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {platformSupport?.additional_info && (
                      <p className="mt-4 text-sm text-slate-600 bg-white/50 p-3 rounded">{platformSupport.additional_info}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Support Tickets */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" /> Support Tickets
                      </CardTitle>
                      <CardDescription>Track your support requests</CardDescription>
                    </div>
                    <Button onClick={() => { setShowCreateTicket(true); fetchSupportTickets(); }} data-testid="vendor-create-ticket-btn">
                      <Plus className="w-4 h-4 mr-2" /> New Ticket
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {supportTickets.length === 0 ? (
                    <div className="text-center py-8">
                      <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No support tickets yet</p>
                      <Button variant="link" onClick={() => { setShowCreateTicket(true); fetchSupportTickets(); }}>Create your first ticket</Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {supportTickets.map((ticket) => (
                        <div
                          key={ticket.id}
                          className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                          onClick={() => setViewTicket(ticket)}
                          data-testid={`vendor-ticket-${ticket.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-slate-400 font-mono">{ticket.ticket_number}</span>
                                <Badge className={`text-xs ${ticket.status === 'open' ? 'bg-blue-100 text-blue-700' : ticket.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                                  {ticket.status}
                                </Badge>
                              </div>
                              <p className="font-medium text-slate-900">{ticket.subject}</p>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.message}</p>
                            </div>
                            <span className="text-xs text-slate-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Expiry Alerts Tab (for Pharma and Inventory vendors) */}
          {(vendor?.vendor_type === 'pharma' || vendor?.vendor_type === 'inventory' || vendor?.vendor_type === 'chemist') && (
            <TabsContent value="expiry-alerts">
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className={`border-l-4 ${(expiryAlerts.summary.expiring_soon_count || 0) > 0 ? 'border-l-yellow-500 bg-yellow-50/50' : 'border-l-green-500'}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(expiryAlerts.summary.expiring_soon_count || 0) > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
                        <Clock className={`w-6 h-6 ${(expiryAlerts.summary.expiring_soon_count || 0) > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Expiring Soon (90 days)</p>
                        <p className="text-2xl font-bold">{expiryAlerts.summary.expiring_soon_count || 0}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={`border-l-4 ${(expiryAlerts.summary.expired_count || 0) > 0 ? 'border-l-red-500 bg-red-50/50' : 'border-l-green-500'}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(expiryAlerts.summary.expired_count || 0) > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                        <AlertCircle className={`w-6 h-6 ${(expiryAlerts.summary.expired_count || 0) > 0 ? 'text-red-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Expired Items</p>
                        <p className="text-2xl font-bold">{expiryAlerts.summary.expired_count || 0}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {expiryAlertsLoading ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-teal-600" />
                      <p className="text-slate-500 mt-2">Loading expiry alerts...</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Expiring Soon Items */}
                    {expiryAlerts.expiring_soon?.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-yellow-700">
                            <Clock className="w-5 h-5" /> Products Expiring Soon at Clinics
                          </CardTitle>
                          <CardDescription>Your products at clinics that will expire within 90 days</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {expiryAlerts.expiring_soon.map((item, i) => (
                              <div key={`el-${i}`} className="p-4 border border-yellow-200 rounded-lg bg-yellow-50/30 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-6 h-6 text-yellow-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-slate-800">{item.product_name}</h3>
                                    <p className="text-sm text-slate-500">
                                      Batch: {item.batch_number || 'N/A'} • Stock: {item.current_stock}
                                    </p>
                                    <Badge className="mt-1 bg-yellow-100 text-yellow-700">
                                      {item.days_until_expiry} days until expiry
                                    </Badge>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium text-slate-700">{item.clinic_name}</p>
                                  <p className="text-sm text-slate-500">{item.doctor_name}</p>
                                  {item.doctor_phone && (
                                    <a href={`tel:${item.doctor_phone}`} className="text-xs text-blue-600 flex items-center justify-end gap-1 mt-1">
                                      <Phone className="w-3 h-3" /> {item.doctor_phone}
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Expired Items */}
                    {expiryAlerts.expired?.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" /> Expired Products at Clinics
                          </CardTitle>
                          <CardDescription>Your products at clinics that have already expired</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {expiryAlerts.expired.map((item, i) => (
                              <div key={`el-${i}`} className="p-4 border border-red-200 rounded-lg bg-red-50/30 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-slate-800">{item.product_name}</h3>
                                    <p className="text-sm text-slate-500">
                                      Batch: {item.batch_number || 'N/A'} • Stock: {item.current_stock}
                                    </p>
                                    <Badge variant="destructive" className="mt-1">
                                      Expired {item.days_since_expiry} days ago
                                    </Badge>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium text-slate-700">{item.clinic_name}</p>
                                  <p className="text-sm text-slate-500">{item.doctor_name}</p>
                                  {item.doctor_phone && (
                                    <a href={`tel:${item.doctor_phone}`} className="text-xs text-blue-600 flex items-center justify-end gap-1 mt-1">
                                      <Phone className="w-3 h-3" /> {item.doctor_phone}
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* No Alerts */}
                    {(!expiryAlerts.expiring_soon?.length && !expiryAlerts.expired?.length) && (
                      <Card>
                        <CardContent className="py-12 text-center">
                          <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                          <h3 className="text-xl font-semibold text-slate-800 mb-2">All Clear!</h3>
                          <p className="text-slate-500">No expiring products at clinics using your inventory</p>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          )}

          {/* My Doctors Tab */}
          <TabsContent value="my-doctors">
            <div className="space-y-6">
              {/* Vendor Link Doctors - Search & Incoming Requests */}
              <VendorLinkDoctors token={localStorage.getItem('vendor_token')} onRefresh={fetchMyDoctors} />
              
              {/* Connected Doctors */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-green-600" /> My Connected Doctors
                      </CardTitle>
                      <CardDescription>Manage your doctor connections and product pricing</CardDescription>
                    </div>
                    <Input placeholder="Search doctors..." className="w-48 h-8 text-xs" onChange={(e) => {
                      const s = e.target.value.toLowerCase();
                      document.querySelectorAll('[data-testid^="doctor-connection-"]').forEach(el => {
                        el.style.display = !s || el.textContent.toLowerCase().includes(s) ? '' : 'none';
                      });
                    }} data-testid="my-doctors-search" />
                  </div>
                </CardHeader>
                <CardContent>
                  {doctorsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : myDoctors.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No connected doctors yet. Search and connect above.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myDoctors.map((mapping) => (
                        <div 
                          key={mapping.id} 
                          className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                          data-testid={`doctor-connection-${mapping.doctor_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                                <User className="w-6 h-6 text-teal-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-800">{mapping.doctor_name}</h3>
                                <p className="text-sm text-slate-500">{mapping.clinic_name || 'Clinic'}</p>
                                {mapping.doctor_specialty && (
                                  <Badge variant="secondary" className="mt-1">{mapping.doctor_specialty}</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">Connected</Badge>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => fetchProductVisibility(mapping)}
                                disabled={productVisibilityLoading}
                                data-testid={`product-visibility-btn-${mapping.doctor_id}`}
                              >
                                {productVisibilityLoading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Package className="w-4 h-4 mr-1" /> Manage Products
                                  </>
                                )}
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openCreditDialog(mapping)}
                                data-testid={`credit-settings-btn-${mapping.doctor_id}`}
                              >
                                <CreditCard className="w-4 h-4 mr-1" /> Credit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-500 border-red-200 hover:bg-red-50"
                                onClick={async () => {
                                  if (!window.confirm('Remove this doctor connection?')) return;
                                  try {
                                    const token = localStorage.getItem('vendor_token');
                                    await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/vendor/unlink/${mapping.id}`, { headers: { Authorization: `Bearer ${token}` }});
                                    toast.success('Connection removed');
                                    fetchMyDoctors();
                                  } catch (err) { toast.error('Failed to remove connection'); }
                                }}
                                data-testid={`unlink-doctor-btn-${mapping.doctor_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remove
                              </Button>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                            {mapping.doctor_email && (
                              <div className="flex items-center gap-2 text-slate-600">
                                <Mail className="w-4 h-4" />
                                <span className="truncate">{mapping.doctor_email}</span>
                              </div>
                            )}
                            {mapping.doctor_phone && (
                              <div className="flex items-center gap-2 text-slate-600">
                                <Phone className="w-4 h-4" />
                                <span>{mapping.doctor_phone}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-slate-400">
                              <Clock className="w-4 h-4" />
                              <span>Connected {new Date(mapping.approved_at || mapping.created_at).toLocaleDateString()}</span>
                            </div>
                            {mapping.credit_enabled && (
                              <div className="flex items-center gap-2 text-blue-600">
                                <CreditCard className="w-4 h-4" />
                                <span>Credit: ₹{mapping.credit_limit?.toLocaleString() || 0} / {mapping.credit_period_days || 30} days</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Wallet Tab */}
          <TabsContent value="wallet">
            <div className="space-y-4">
              <VendorWalletTab />
              <WalletRedemptionSection 
                userType="vendor" 
                token={localStorage.getItem('vendor_token')} 
                walletBalance={vendorWalletBalance}
                onRedemptionSuccess={() => {
                  axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-wallet/balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('vendor_token')}` }}).then(r => setVendorWalletBalance(r.data?.balance || 0)).catch(() => {});
                }}
              />
            </div>
          </TabsContent>

          {/* Referral Ledger Tab */}
          <TabsContent value="referral-ledger">
            <VendorReferralLedger vendorToken={localStorage.getItem('vendor_token')} />
          </TabsContent>

          {/* Vendor Billing Tab (all vendor types) */}
          <TabsContent value="walkin-billing">
            <ChemistBillingTab token={localStorage.getItem('vendor_token')} vendorType={vendor?.vendor_type} rxBarcode={pendingRxBarcode} onRxProcessed={() => setPendingRxBarcode(null)} />
          </TabsContent>

          {/* Vendor Inventory Tab (all vendor types) */}
          <TabsContent value="chemist-inventory">
            <ChemistInventoryTab token={localStorage.getItem('vendor_token')} vendorType={vendor?.vendor_type} />
          </TabsContent>

          {/* My Vendors Tab (Chemist ↔ Pharma) */}
          {['chemist', 'pharma'].includes(vendor?.vendor_type) && (
            <TabsContent value="my-vendors">
              <VendorLinkVendors token={localStorage.getItem('vendor_token')} vendorType={vendor?.vendor_type} />
            </TabsContent>
          )}

          {/* Buy from Pharma Tab (Chemist only) */}
          {vendor?.vendor_type === 'chemist' && (
            <TabsContent value="buy-pharma">
              <ChemistMarketplace token={localStorage.getItem('vendor_token')} />
            </TabsContent>
          )}

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <UserCircle className="w-5 h-5 text-blue-600" />
                        My Profile
                      </CardTitle>
                      <CardDescription>Manage your business and MR/Sales executive details</CardDescription>
                    </div>
                    {!editingProfile ? (
                      <Button variant="outline" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn">
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => { setEditingProfile(false); setProfileForm({ name: vendor?.name || '', email: vendor?.email || '', phone: vendor?.phone || '', address: vendor?.address || '', mr_name: vendor?.mr_name || '', mr_phone: vendor?.mr_phone || '', mr_email: vendor?.mr_email || '', mr_territory: vendor?.mr_territory || '' }); }}>
                          Cancel
                        </Button>
                        <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                          {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Changes
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company Logo Upload */}
                  <div className="flex items-center gap-6 pb-4 border-b">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                        {vendor?.logo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${vendor.logo_url}`} alt="Company Logo" className="w-full h-full object-cover" />
                        ) : (
                          <Building2 className="w-10 h-10 text-slate-400" />
                        )}
                      </div>
                      {editingProfile && (
                        <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const token = localStorage.getItem('vendor_token');
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/vendor-logo`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setVendor({...vendor, logo_url: data.url});
                                  toast.success('Logo uploaded successfully');
                                } else {
                                  const err = await res.json();
                                  toast.error(err.detail || 'Failed to upload logo');
                                }
                              } catch (err) {
                                toast.error('Failed to upload logo');
                              }
                            }}
                          />
                          <Image className="w-4 h-4 text-white" />
                        </label>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{vendor?.name}</h3>
                      <p className="text-sm text-slate-500 capitalize">{vendor?.vendor_type} Portal</p>
                      {editingProfile && <p className="text-xs text-blue-600 mt-1">Click the icon to upload a logo</p>}
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                      <Building2 className="w-4 h-4 text-blue-600" /> Business Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-500 text-xs">Business Name</Label>
                        {editingProfile ? (
                          <Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} className="mt-1" />
                        ) : (
                          <p className="font-medium text-slate-900">{vendor?.name || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Vendor Type</Label>
                        <p className="font-medium text-slate-900 capitalize">{vendor?.vendor_type || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Email</Label>
                        {editingProfile ? (
                          <Input value={profileForm.email} onChange={(e) => setProfileForm({...profileForm, email: e.target.value})} className="mt-1" type="email" />
                        ) : (
                          <p className="font-medium text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {vendor?.email || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Phone</Label>
                        {editingProfile ? (
                          <Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} className="mt-1" />
                        ) : (
                          <p className="font-medium text-slate-900 flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {vendor?.phone || '-'}</p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-500 text-xs">Address</Label>
                        {editingProfile ? (
                          <Textarea value={profileForm.address} onChange={(e) => setProfileForm({...profileForm, address: e.target.value})} className="mt-1" rows={2} />
                        ) : (
                          <p className="font-medium text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {vendor?.address || '-'}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Additional Business Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <Label className="text-slate-500 text-xs">GST Number</Label>
                        {editingProfile ? (
                          <Input value={profileForm.gst_number} onChange={(e) => setProfileForm({...profileForm, gst_number: e.target.value})} className="mt-1 font-mono" placeholder="e.g. 27AADCB2230M1ZV" data-testid="vendor-gst-number-input" />
                        ) : (
                          <p className="font-mono text-sm text-slate-900">{vendor?.gst_number || '-'}</p>
                        )}
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <Label className="text-slate-500 text-xs">GST Percentage (%)</Label>
                        {editingProfile ? (
                          <Input type="number" min="0" max="100" step="0.01" value={profileForm.gst_percentage} onChange={(e) => setProfileForm({...profileForm, gst_percentage: e.target.value})} className="mt-1" placeholder="e.g. 18" data-testid="vendor-gst-percentage-input" />
                        ) : (
                          <p className="text-sm text-slate-900">{vendor?.gst_percentage != null ? `${vendor.gst_percentage}%` : '-'}</p>
                        )}
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <Label className="text-slate-500 text-xs">License Number</Label>
                        {editingProfile ? (
                          <Input value={profileForm.license_number || ''} onChange={(e) => setProfileForm({...profileForm, license_number: e.target.value})} className="mt-1 font-mono" placeholder="License number" data-testid="vendor-license-input" />
                        ) : (
                          <p className="font-mono text-sm text-slate-900">{vendor?.license_number || '-'}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <Label className="text-slate-500 text-xs">Account Status</Label>
                        <Badge className={vendor?.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {vendor?.status || 'pending'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* MR/Sales Executive Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                      <Briefcase className="w-4 h-4 text-purple-600" /> MR/Sales Executive Details
                    </h3>
                    {(vendor?.mr_name || vendor?.mr_phone || vendor?.mr_email || editingProfile) ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-500 text-xs">MR Name</Label>
                          {editingProfile ? (
                            <Input value={profileForm.mr_name} onChange={(e) => setProfileForm({...profileForm, mr_name: e.target.value})} className="mt-1" placeholder="Sales executive name" />
                          ) : (
                            <p className="font-medium text-slate-900 flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> {vendor?.mr_name || '-'}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-slate-500 text-xs">MR Phone</Label>
                          {editingProfile ? (
                            <Input value={profileForm.mr_phone} onChange={(e) => setProfileForm({...profileForm, mr_phone: e.target.value})} className="mt-1" placeholder="Sales executive phone" />
                          ) : (
                            <p className="font-medium text-slate-900 flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {vendor?.mr_phone || '-'}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-slate-500 text-xs">MR Email</Label>
                          {editingProfile ? (
                            <Input value={profileForm.mr_email} onChange={(e) => setProfileForm({...profileForm, mr_email: e.target.value})} className="mt-1" placeholder="Sales executive email" type="email" />
                          ) : (
                            <p className="font-medium text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {vendor?.mr_email || '-'}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-slate-500 text-xs">Territory/Region</Label>
                          {editingProfile ? (
                            <Input value={profileForm.mr_territory} onChange={(e) => setProfileForm({...profileForm, mr_territory: e.target.value})} className="mt-1" placeholder="Coverage area" />
                          ) : (
                            <p className="font-medium text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {vendor?.mr_territory || '-'}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-slate-50 rounded-lg">
                        <Briefcase className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No MR/Sales executive details added</p>
                        <Button variant="link" className="mt-2" onClick={() => setEditingProfile(true)}>
                          + Add MR Details
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Account Info */}
                  <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-500">
                    <p>Account created: {vendor?.created_at ? new Date(vendor.created_at).toLocaleDateString() : '-'}</p>
                    <p className="mt-1">Vendor ID: <span className="font-mono">{vendor?.id}</span></p>
                  </div>

                  {/* Service Settings (Location, Radius, Hours) */}
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-sm text-slate-700 mb-3">Service Settings</h3>
                    <VendorServiceSettings token={localStorage.getItem('vendor_token')} />
                  </div>

                  {/* Saved Addresses */}
                  <div className="pt-4 border-t">
                    <SavedAddresses token={localStorage.getItem('vendor_token')} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload Report Dialog */}
      <Dialog open={showUploadReport} onOpenChange={setShowUploadReport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Lab Report(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Order ID *</Label>
              <Select value={reportUpload.order_id} onValueChange={(v) => {
                const allOrders = [...orders, ...patientPortalOrders];
                const order = allOrders.find(o => o.id === v);
                setReportUpload({ ...reportUpload, order_id: v, patient_name: order?.patient_name || '', patient_phone: order?.patient_phone || '' });
              }}>
                <SelectTrigger><SelectValue placeholder="Select Order" /></SelectTrigger>
                <SelectContent>
                  {/* B2B Orders */}
                  {orders.filter(o => o.status !== 'cancelled').length > 0 && (
                    <div className="px-2 py-1 text-xs text-slate-500 font-medium">B2B Orders</div>
                  )}
                  {orders.filter(o => o.status !== 'cancelled').map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.order_number || o.order_id} - {o.patient_name || 'No patient'}</SelectItem>
                  ))}
                  {/* Patient Portal Orders */}
                  {patientPortalOrders.filter(o => o.status !== 'cancelled').length > 0 && (
                    <div className="px-2 py-1 text-xs text-slate-500 font-medium">Patient Orders</div>
                  )}
                  {patientPortalOrders.filter(o => o.status !== 'cancelled').map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.order_number} - {o.patient_name || 'No patient'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Patient Name</Label>
              <Input value={reportUpload.patient_name} onChange={(e) => setReportUpload({ ...reportUpload, patient_name: e.target.value })} />
            </div>
            <div>
              <Label>Patient Phone</Label>
              <div className="flex gap-2">
                <Input 
                  value={reportUpload.patient_phone} 
                  onChange={(e) => setReportUpload({ ...reportUpload, patient_phone: e.target.value })} 
                  placeholder="e.g., +919876543210" 
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    if (!reportUpload.patient_phone) {
                      toast.error('Enter phone number first');
                      return;
                    }
                    try {
                      const res = await VendorAPI.get(`/patient-marketplace/vendor/lookup-patient?phone=${encodeURIComponent(reportUpload.patient_phone)}`);
                      if (res.data.found) {
                        setReportUpload({ ...reportUpload, patient_name: res.data.patient.name });
                        toast.success(`Found: ${res.data.patient.name}`);
                      } else {
                        toast.error('Patient not found');
                      }
                    } catch (err) {
                      toast.error('Lookup failed');
                    }
                  }}
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Single Report Upload */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Option 1: Single Report</p>
              <div className="space-y-3">
                <div>
                  <Label>Report Title *</Label>
                  <Input value={reportUpload.report_title} onChange={(e) => setReportUpload({ ...reportUpload, report_title: e.target.value })} placeholder="e.g., CBC Report, Lipid Profile" />
                </div>
                <div>
                  <Label>Report File (PDF/PNG/JPEG) *</Label>
                  <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setReportUpload({ ...reportUpload, file: e.target.files?.[0] || null })} />
                </div>
                <Button onClick={uploadLabReport} disabled={!reportUpload.order_id || !reportUpload.report_title || !reportUpload.file} className="w-full">
                  Upload Single Report
                </Button>
              </div>
            </div>

            {/* Multiple Reports Upload */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Option 2: Multiple Reports</p>
              <p className="text-xs text-slate-500 mb-3">Select multiple files. Each file name will be used as the report title.</p>
              <div className="space-y-3">
                <div>
                  <Label>Report Files (PDF/PNG/JPEG)</Label>
                  <Input 
                    type="file" 
                    accept=".pdf,.png,.jpg,.jpeg" 
                    multiple
                    onChange={(e) => setMultipleReportFiles(Array.from(e.target.files || []))}
                  />
                  {multipleReportFiles.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{multipleReportFiles.length} file(s) selected</p>
                  )}
                </div>
                <Button 
                  onClick={() => handleMultipleReportUpload(reportUpload.order_id, multipleReportFiles)} 
                  disabled={!reportUpload.order_id || multipleReportFiles.length === 0 || uploadingMultipleReports}
                  className="w-full"
                  variant="secondary"
                >
                  {uploadingMultipleReports ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <>Upload {multipleReportFiles.length || ''} Report{multipleReportFiles.length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Support Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <Label>Subject *</Label>
              <Input
                data-testid="vendor-ticket-subject"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                placeholder="Brief description of your issue"
                className="mt-1"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={ticketForm.category} onValueChange={(v) => setTicketForm({ ...ticketForm, category: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Inquiry</SelectItem>
                    <SelectItem value="technical">Technical Issue</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="orders">Orders</SelectItem>
                    <SelectItem value="marketplace">Marketplace</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm({ ...ticketForm, priority: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                data-testid="vendor-ticket-message"
                value={ticketForm.message}
                onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                placeholder="Describe your issue in detail..."
                className="mt-1"
                rows={4}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
              <Button type="submit" disabled={ticketLoading} data-testid="vendor-submit-ticket-btn">
                {ticketLoading ? 'Creating...' : 'Create Ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={() => setViewTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewTicket && (
            <>
              <DialogHeader>
                <DialogTitle>
                  <span className="text-xs text-slate-400 font-mono block mb-1">{viewTicket.ticket_number}</span>
                  {viewTicket.subject}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge className={viewTicket.status === 'open' ? 'bg-blue-100 text-blue-700' : viewTicket.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                    {viewTicket.status}
                  </Badge>
                  <Badge variant="outline">{viewTicket.category}</Badge>
                  <Badge variant="outline">{viewTicket.priority}</Badge>
                </div>
                
                {/* Messages Thread */}
                <div className="space-y-3 border rounded-lg p-4 bg-slate-50 max-h-[300px] overflow-y-auto">
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-slate-900">You</span>
                      <span className="text-xs text-slate-400">{new Date(viewTicket.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{viewTicket.message}</p>
                  </div>
                  
                  {viewTicket.responses?.map((response) => (
                    <div key={response.id} className={`p-3 rounded-lg border ${response.is_admin ? 'bg-teal-50 border-teal-200' : 'bg-white'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-sm font-medium ${response.is_admin ? 'text-teal-700' : 'text-slate-900'}`}>
                          {response.is_admin ? '1Clin Support' : 'You'}
                        </span>
                        <span className="text-xs text-slate-400">{new Date(response.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Box */}
                {viewTicket.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows={2}
                      className="flex-1"
                      data-testid="vendor-ticket-reply"
                    />
                    <Button onClick={handleTicketReply} disabled={ticketLoading || !replyText.trim()}>
                      {ticketLoading ? '...' : 'Send'}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCoupon} className="space-y-4">
            <div>
              <Label>Coupon Code *</Label>
              <Input
                data-testid="coupon-code-input"
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SAVE20"
                className="mt-1 font-mono"
                disabled={!!editingCoupon}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={couponForm.description}
                onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                placeholder="e.g., 20% off on first order"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  value={couponForm.discount_value || ''}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Order (₹)</Label>
                <Input
                  type="number"
                  value={couponForm.min_order_value || ''}
                  onChange={(e) => setCouponForm({ ...couponForm, min_order_value: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  placeholder="0 = no minimum"
                />
              </div>
              <div>
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  value={couponForm.max_uses || ''}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  placeholder="0 = unlimited"
                />
              </div>
            </div>
            <div>
              <Label>Valid Until (optional)</Label>
              <Input
                type="date"
                value={couponForm.valid_until ? couponForm.valid_until.split('T')[0] : ''}
                onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={couponForm.is_active}
                onCheckedChange={(v) => setCouponForm({ ...couponForm, is_active: v })}
              />
              <Label>Coupon Active</Label>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Applicable Subscription Tiers (optional)</Label>
              <div className="flex flex-wrap gap-2">
                {['weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'].map(tier => {
                  const selected = (couponForm.applicable_tiers || []).includes(tier);
                  return (
                    <button key={tier} type="button"
                      className={`text-xs px-2.5 py-1 rounded-full border ${selected ? 'bg-violet-100 border-violet-400 text-violet-700' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'}`}
                      onClick={() => {
                        const tiers = couponForm.applicable_tiers || [];
                        setCouponForm({ ...couponForm, applicable_tiers: selected ? tiers.filter(t => t !== tier) : [...tiers, tier] });
                      }}>
                      {tier.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Leave empty to apply to all tiers</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCouponDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" data-testid="save-coupon-btn">{editingCoupon ? 'Update' : 'Create'} Coupon</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Import from Excel/CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload an Excel (.xlsx) or CSV file to import multiple items at once.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Required columns:</p>
              <p className="text-xs text-slate-500">
                {vendor?.vendor_type === 'lab' 
                  ? 'name, description, category, sample_type, turnaround_time, mrp, b2b_price, home_collection'
                  : (vendor?.vendor_type === 'pharma' || vendor?.vendor_type === 'chemist')
                  ? 'name, generic_name, composition, manufacturer, category, strength, pack_size, mrp, b2b_price, prescription_required'
                  : 'name, description, category, brand, model_number, unit, mrp, b2b_price, min_order_quantity, warranty_months'
                }
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={downloadBulkTemplate}>
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                id="bulk-import-file"
                onChange={(e) => handleBulkImport(e.target.files?.[0])}
              />
              <label htmlFor="bulk-import-file" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Click to select file</p>
                <p className="text-xs text-slate-400">.xlsx, .xls, or .csv</p>
              </label>
            </div>
            {bulkImportLoading && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Importing...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Doctor Connection Dialog */}
      <Dialog open={showRequestDoctorDialog} onOpenChange={setShowRequestDoctorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Doctor Connection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select Doctor</Label>
              <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a doctor to connect with..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDoctors.filter(d => d.connection_status === 'not_connected').map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.clinic_name || 'Clinic'} {d.specialty && `(${d.specialty})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableDoctors.filter(d => d.connection_status === 'not_connected').length === 0 && (
                <p className="text-xs text-amber-600 mt-2">No new doctors available to connect with</p>
              )}
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={requestDoctorNotes}
                onChange={(e) => setRequestDoctorNotes(e.target.value)}
                placeholder="Any notes for the admin reviewing your request..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Your request will be sent to the platform admin for approval. Once approved, you can manage product visibility for this doctor.
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowRequestDoctorDialog(false); setSelectedDoctorId(''); setRequestDoctorNotes(''); }}>
                Cancel
              </Button>
              <Button onClick={requestDoctorConnection} disabled={requestingDoctor || !selectedDoctorId}>
                {requestingDoctor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Visibility Management Dialog */}
      <Dialog open={productVisibilityDialog.open} onOpenChange={(open) => { if (!open) { setProductVisibilityDialog({ open: false, doctor: null, products: [] }); setProductSearchQuery(''); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Products & Pricing</DialogTitle>
            {productVisibilityDialog.doctor && (
              <p className="text-sm text-slate-500 mt-1">
                Set visibility and custom B2B prices for Dr. {productVisibilityDialog.doctor.doctor_name}
              </p>
            )}
          </DialogHeader>
          {/* Search bar */}
          {productVisibilityDialog.products.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search products by name, category..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="product-search-input"
              />
            </div>
          )}
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {productVisibilityDialog.products.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No products in your catalog yet.</p>
                <p className="text-sm">Add products to your catalog first, then manage visibility here.</p>
              </div>
            ) : (
              productVisibilityDialog.products
                .filter(product => {
                  if (!productSearchQuery) return true;
                  const q = productSearchQuery.toLowerCase();
                  return (product.product_name || '').toLowerCase().includes(q) ||
                         (product.category || '').toLowerCase().includes(q) ||
                         (product.generic_name || '').toLowerCase().includes(q);
                })
                .map((product) => (
                <div 
                  key={product.product_id} 
                  className={`p-3 rounded-lg border ${product.visible ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                  data-testid={`product-row-${product.product_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{product.product_name}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <span>Default B2B: ₹{product.b2b_price}</span>
                        {product.mrp && <span className="line-through">MRP: ₹{product.mrp}</span>}
                        {product.category && <Badge variant="secondary">{product.category}</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${product.visible ? 'text-green-600' : 'text-slate-400'}`}>
                        {product.visible ? 'Visible' : 'Hidden'}
                      </span>
                      <Switch
                        checked={product.visible}
                        onCheckedChange={(v) => updateProductVisibility(product.product_id, v)}
                      />
                    </div>
                  </div>
                  {product.visible && (
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Custom Price (₹):</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={`${product.b2b_price} (default)`}
                        value={product.custom_price ?? ''}
                        onChange={(e) => updateProductCustomPrice(product.product_id, e.target.value)}
                        className="h-8 w-32 text-sm"
                        data-testid={`custom-price-${product.product_id}`}
                      />
                      {product.custom_price != null && product.custom_price !== '' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs text-red-500"
                          onClick={() => updateProductCustomPrice(product.product_id, '')}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-slate-500">
              {productVisibilityDialog.products.filter(p => p.visible).length} of {productVisibilityDialog.products.length} products visible
              {productSearchQuery && (
                <span className="ml-2 text-blue-600">
                  | Showing {productVisibilityDialog.products.filter(p => {
                    const q = productSearchQuery.toLowerCase();
                    return (p.product_name || '').toLowerCase().includes(q) ||
                           (p.category || '').toLowerCase().includes(q) ||
                           (p.generic_name || '').toLowerCase().includes(q);
                  }).length} results
                </span>
              )}
              {productVisibilityDialog.products.filter(p => p.custom_price != null && p.custom_price !== '').length > 0 && (
                <span className="ml-2 text-blue-600">
                  | {productVisibilityDialog.products.filter(p => p.custom_price != null && p.custom_price !== '').length} custom prices
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setProductVisibilityDialog({ open: false, doctor: null, products: [] })}>
                Cancel
              </Button>
              <Button onClick={saveProductVisibility} disabled={savingProductVisibility}>
                {savingProductVisibility ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Dialog */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-sm">{lightboxPhoto?.name}</DialogTitle>
          </DialogHeader>
          {lightboxPhoto && (
            <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
              <img 
                src={lightboxPhoto.url} 
                alt={lightboxPhoto.name} 
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddBankDialog} onOpenChange={setShowAddBankDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Account Holder Name *</Label>
              <Input
                value={bankForm.account_holder_name}
                onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                placeholder="Enter name as per bank"
                data-testid="bank-holder-name"
              />
            </div>
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                placeholder="e.g., HDFC Bank"
                data-testid="bank-name"
              />
            </div>
            <div>
              <Label>Account Number *</Label>
              <Input
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                placeholder="Enter account number"
                data-testid="bank-account-number"
              />
            </div>
            <div>
              <Label>IFSC Code *</Label>
              <Input
                value={bankForm.ifsc_code}
                onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value.toUpperCase() })}
                placeholder="e.g., HDFC0001234"
                data-testid="bank-ifsc"
              />
            </div>
            <div>
              <Label>Account Type</Label>
              <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={bankForm.is_primary} 
                onCheckedChange={(v) => setBankForm({ ...bankForm, is_primary: v })}
              />
              <Label>Set as primary account</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBankDialog(false)}>Cancel</Button>
            <Button onClick={addBankAccount} disabled={submittingBank} data-testid="save-bank-btn">
              {submittingBank ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-600" /> Request Payout
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-sm text-emerald-600">Available Balance</p>
              <p className="text-3xl font-bold text-emerald-700">₹{earningsSummary?.available_for_payout?.toLocaleString() || 0}</p>
            </div>
            <div>
              <Label>Payout Amount (₹) *</Label>
              <Input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                max={earningsSummary?.available_for_payout || 0}
                data-testid="payout-amount"
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder="Any additional notes for admin"
                rows={2}
              />
            </div>
            {bankAccounts.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Payment will be sent to:</p>
                <p className="font-medium text-sm">{bankAccounts.find(a => a.is_primary)?.bank_name || bankAccounts[0]?.bank_name}</p>
                <p className="text-xs text-slate-600">{bankAccounts.find(a => a.is_primary)?.account_number_masked || bankAccounts[0]?.account_number_masked}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>Cancel</Button>
            <Button onClick={requestPayout} disabled={submittingPayout} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-payout-btn">
              {submittingPayout ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Settings Dialog */}
      <Dialog open={creditDialog.open} onOpenChange={(open) => setCreditDialog({ ...creditDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" /> Credit Settings
            </DialogTitle>
            <DialogDescription>
              Set credit terms for {creditDialog.mapping?.doctor_name || 'this doctor'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Enable Credit Facility</Label>
                <p className="text-xs text-muted-foreground">Allow this doctor to order on credit</p>
              </div>
              <Switch checked={creditEnabled} onCheckedChange={setCreditEnabled} data-testid="credit-enabled-switch" />
            </div>
            {creditEnabled && (
              <>
                <div>
                  <Label className="text-sm">Credit Limit (₹)</Label>
                  <Input
                    type="number" min="0" step="100" value={creditLimit}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    placeholder="e.g. 10000"
                    data-testid="credit-limit-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Max credit amount. 0 = unlimited</p>
                </div>
                <div>
                  <Label className="text-sm">Credit Period (Days)</Label>
                  <Input
                    type="number" min="1" max="365" value={creditPeriodDays}
                    onChange={(e) => setCreditPeriodDays(e.target.value)}
                    placeholder="e.g. 30"
                    data-testid="credit-period-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Auto-debit from wallet after this many days</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialog({ open: false, mapping: null })}>Cancel</Button>
            <Button onClick={saveCreditSettings} disabled={savingCredit} data-testid="save-credit-settings-btn">
              {savingCredit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Credit as Paid Dialog */}
      <Dialog open={!!markPaidOrder} onOpenChange={(v) => !v && setMarkPaidOrder(null)}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark Credit as Paid</DialogTitle>
            <DialogDescription>
              Record that {markPaidOrder?.clinic_name || markPaidOrder?.doctor_name || 'the doctor'} paid ₹{markPaidOrder?.total?.toLocaleString()} offline. This does not touch any wallet balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Payment Method</Label>
              <Select value={markPaidMethod} onValueChange={setMarkPaidMethod}>
                <SelectTrigger className="mt-1" data-testid="mark-paid-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Note (optional)</Label>
              <Textarea value={markPaidNote} onChange={(e) => setMarkPaidNote(e.target.value)}
                placeholder="e.g. UPI ref no., received by..." className="mt-1" data-testid="mark-paid-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOrder(null)}>Cancel</Button>
            <Button onClick={handleMarkCreditPaid} disabled={markingPaid} data-testid="mark-paid-confirm-btn">
              {markingPaid ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Logout
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You will need to log in again to access your vendor account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLogout}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmLogout} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Product Dialog Component
function ProductDialog({ vendorType, product, onSave, onClose, open, setOpen, trigger, uploadProductPhoto }) {
  const isEdit = !!product;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({ ...product });
    } else {
      setForm(getDefaultForm(vendorType));
    }
  }, [product, vendorType]);

  const getDefaultForm = (type) => {
    switch (type) {
      case 'lab': return { name: '', description: '', category: '', sample_type: '', turnaround_time: '', mrp: 0, b2b_price: 0, patient_mrp: 0, home_collection: false, instructions: '' };
      case 'pharma':
      case 'chemist': return { name: '', generic_name: '', composition: '', manufacturer: '', category: '', strength: '', pack_size: '', mrp: 0, b2b_price: 0, patient_mrp: 0, prescription_required: true, storage_instructions: '', photo_url: '', price_tiers: {}, batch_expiry: '', is_subscription: false, subscription_pricing: { weekly: 0, monthly: 0, quarterly: 0, half_yearly: 0, yearly: 0 } };
      case 'inventory': return { name: '', description: '', category: '', brand: '', model_number: '', unit: 'piece', mrp: 0, b2b_price: 0, patient_mrp: 0, min_order_quantity: 1, warranty_months: 0, specifications: '', photo_url: '', price_tiers: {}, batch_expiry: '', is_subscription: false, subscription_pricing: { weekly: 0, monthly: 0, quarterly: 0, half_yearly: 0, yearly: 0 } };
      case 'marketing': return { name: '', description: '', category: '', service_type: '', mrp: 0, b2b_price: 0, deliverables: '', turnaround_time: '', photo_url: '', is_subscription: false, subscription_pricing: { weekly: 0, monthly: 0, quarterly: 0, half_yearly: 0, yearly: 0 } };
      default: return {};
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !uploadProductPhoto) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadProductPhoto(file);
      if (url) {
        setForm({ ...form, photo_url: url });
        toast.success('Photo uploaded');
      } else {
        toast.error('Photo upload failed');
      }
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = vendorType === 'lab' ? '/lab-tests' : (vendorType === 'pharma' || vendorType === 'chemist') ? '/medicines' : vendorType === 'marketing' ? '/marketing-services' : '/inventory';
      if (isEdit) {
        await VendorAPI.put(`${endpoint}/${product.id}`, form);
        toast.success('Item updated');
      } else {
        await VendorAPI.post(endpoint, form);
        toast.success('Item added');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const renderFields = () => {
    switch (vendorType) {
      case 'lab':
        return (
          <>
            <div className="space-y-2"><Label>Test Name *</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label><Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="space-y-2"><Label>Sample Type</Label><Input value={form.sample_type || ''} onChange={(e) => setForm({ ...form, sample_type: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Turnaround Time</Label><Input value={form.turnaround_time || ''} onChange={(e) => setForm({ ...form, turnaround_time: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>MRP (₹) *</Label><Input type="number" value={form.mrp || ''} onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>B2B Price (₹) *</Label><Input type="number" value={form.b2b_price || ''} onChange={(e) => setForm({ ...form, b2b_price: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>Patient Price (₹)</Label><Input type="number" value={form.patient_mrp || ''} onChange={(e) => setForm({ ...form, patient_mrp: parseFloat(e.target.value) || 0 })} placeholder="Same as MRP if empty" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.home_collection || false} onCheckedChange={(v) => setForm({ ...form, home_collection: v })} /><Label>Home Collection</Label></div>
            <div className="space-y-2"><Label>Instructions</Label><Textarea value={form.instructions || ''} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></div>
          </>
        );
      case 'pharma':
      case 'chemist':
        return (
          <>
            <div className="space-y-2"><Label>Medicine Name *</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Generic Name</Label><Input value={form.generic_name || ''} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Manufacturer</Label><Input value={form.manufacturer || ''} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Composition</Label><Input value={form.composition || ''} onChange={(e) => setForm({ ...form, composition: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Category</Label><Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="space-y-2"><Label>Strength</Label><Input value={form.strength || ''} onChange={(e) => setForm({ ...form, strength: e.target.value })} /></div>
              <div className="space-y-2"><Label>Pack Size</Label><Input value={form.pack_size || ''} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>MRP (₹) *</Label><Input type="number" value={form.mrp || ''} onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>B2B Price (₹) *</Label><Input type="number" value={form.b2b_price || ''} onChange={(e) => setForm({ ...form, b2b_price: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>Patient Price (₹)</Label><Input type="number" value={form.patient_mrp || ''} onChange={(e) => setForm({ ...form, patient_mrp: parseFloat(e.target.value) || 0 })} placeholder="Same as MRP if empty" /></div>
            </div>
            <div className="space-y-2"><Label>Barcode / SKU</Label><Input value={form.barcode || ''} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or enter barcode for quick billing" data-testid="product-barcode-input" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.prescription_required ?? true} onCheckedChange={(v) => setForm({ ...form, prescription_required: v })} /><Label>Prescription Required</Label></div>
            {/* Subscription Pricing */}
            <SubscriptionPricingEditor pricing={form.subscription_pricing || {}} isSubscription={form.is_subscription} onChange={(sp, isSub) => setForm({ ...form, subscription_pricing: sp, is_subscription: isSub })} />
            {/* Price Tiers */}
            <PriceTiersEditor priceTiers={form.price_tiers || {}} onChange={(tiers) => setForm({ ...form, price_tiers: tiers })} />
            {/* Batch Expiry */}
            <div className="space-y-2">
              <Label>Batch Expiry Date</Label>
              <Input type="date" value={form.batch_expiry ? form.batch_expiry.split('T')[0] : ''} onChange={(e) => setForm({ ...form, batch_expiry: e.target.value })} />
            </div>
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Product Photo</Label>
              <div className="flex items-center gap-3">
                {form.photo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="Product" className="w-16 h-16 object-cover rounded-lg border" />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  id="pharma-photo-upload"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                />
                <label htmlFor="pharma-photo-upload" className="cursor-pointer border rounded-lg px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  {uploadingPhoto ? 'Uploading...' : form.photo_url ? 'Change Photo' : 'Add Photo'}
                </label>
              </div>
            </div>
          </>
        );
      case 'inventory':
        return (
          <>
            <div className="space-y-2"><Label>Product Name *</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Brand</Label><Input value={form.brand || ''} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
              <div className="space-y-2"><Label>Model Number</Label><Input value={form.model_number || ''} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label><Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="space-y-2"><Label>Unit</Label><Input value={form.unit || ''} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>MRP (₹) *</Label><Input type="number" value={form.mrp || ''} onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>B2B Price (₹) *</Label><Input type="number" value={form.b2b_price || ''} onChange={(e) => setForm({ ...form, b2b_price: parseFloat(e.target.value) })} required /></div>
              <div className="space-y-2"><Label>Patient Price (₹)</Label><Input type="number" value={form.patient_mrp || ''} onChange={(e) => setForm({ ...form, patient_mrp: parseFloat(e.target.value) || 0 })} placeholder="Same as MRP if empty" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Min Order Qty</Label><Input type="number" value={form.min_order_quantity || 1} onChange={(e) => setForm({ ...form, min_order_quantity: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Warranty (months)</Label><Input type="number" value={form.warranty_months || 0} onChange={(e) => setForm({ ...form, warranty_months: parseInt(e.target.value) })} /></div>
            </div>
            <div className="space-y-2"><Label>Barcode / SKU</Label><Input value={form.barcode || ''} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or enter barcode for quick billing" data-testid="product-barcode-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description || form.specifications || ''} onChange={(e) => setForm({ ...form, description: e.target.value, specifications: e.target.value })} /></div>
            {/* Subscription Pricing */}
            <SubscriptionPricingEditor pricing={form.subscription_pricing || {}} isSubscription={form.is_subscription} onChange={(sp, isSub) => setForm({ ...form, subscription_pricing: sp, is_subscription: isSub })} />
            {/* Price Tiers */}
            <PriceTiersEditor priceTiers={form.price_tiers || {}} onChange={(tiers) => setForm({ ...form, price_tiers: tiers })} />
            {/* Batch Expiry */}
            <div className="space-y-2">
              <Label>Batch Expiry Date (for consumables)</Label>
              <Input type="date" value={form.batch_expiry ? form.batch_expiry.split('T')[0] : ''} onChange={(e) => setForm({ ...form, batch_expiry: e.target.value })} />
            </div>
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Product Photo</Label>
              <div className="flex items-center gap-3">
                {form.photo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="Product" className="w-16 h-16 object-cover rounded-lg border" />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  id="inventory-photo-upload"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                />
                <label htmlFor="inventory-photo-upload" className="cursor-pointer border rounded-lg px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  {uploadingPhoto ? 'Uploading...' : form.photo_url ? 'Change Photo' : 'Add Photo'}
                </label>
              </div>
            </div>
          </>
        );
      case 'marketing':
        return (
          <>
            <div className="space-y-2"><Label>Service Name *</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="mkt-service-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category || ''} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seo">SEO</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="content">Content Marketing</SelectItem>
                    <SelectItem value="ppc">PPC / Google Ads</SelectItem>
                    <SelectItem value="web_design">Web Design</SelectItem>
                    <SelectItem value="branding">Branding</SelectItem>
                    <SelectItem value="banner">Banners</SelectItem>
                    <SelectItem value="flyer">Flyers / Print</SelectItem>
                    <SelectItem value="video">Video Production</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select value={form.service_type || ''} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>MRP (₹)</Label><Input type="number" value={form.mrp || ''} onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) || 0 })} /></div>
              <div className="space-y-2"><Label>B2B Price (₹) *</Label><Input type="number" value={form.b2b_price || ''} onChange={(e) => setForm({ ...form, b2b_price: parseFloat(e.target.value) })} required /></div>
            </div>
            <div className="space-y-2"><Label>Deliverables</Label><Input value={form.deliverables || ''} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} placeholder="What the doctor gets" /></div>
            <div className="space-y-2"><Label>Turnaround Time</Label><Input value={form.turnaround_time || ''} onChange={(e) => setForm({ ...form, turnaround_time: e.target.value })} placeholder="e.g. 3-5 business days" /></div>
            {/* Subscription Pricing */}
            <SubscriptionPricingEditor pricing={form.subscription_pricing || {}} isSubscription={form.is_subscription} onChange={(sp, isSub) => setForm({ ...form, subscription_pricing: sp, is_subscription: isSub })} />
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Service Photo</Label>
              <div className="flex items-center gap-3">
                {form.photo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="Service" className="w-16 h-16 object-cover rounded-lg border" />
                )}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" id="marketing-photo-upload"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
                <label htmlFor="marketing-photo-upload" className="cursor-pointer border rounded-lg px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  {uploadingPhoto ? 'Uploading...' : form.photo_url ? 'Change Photo' : 'Add Photo'}
                </label>
              </div>
            </div>
          </>
        );
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Item' : 'Add New Item'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {renderFields()}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={loading}>{loading ? 'Saving...' : isEdit ? 'Update' : 'Add Item'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Subscription Pricing Editor Component
function SubscriptionPricingEditor({ pricing, isSubscription, onChange }) {
  const periods = [
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'half_yearly', label: 'Half Yearly' },
    { key: 'yearly', label: 'Yearly' }
  ];
  return (
    <div className="space-y-3 border rounded-lg p-3 bg-violet-50/30">
      <div className="flex items-center gap-2">
        <Switch checked={isSubscription || false} onCheckedChange={(v) => onChange(pricing, v)} />
        <Label className="text-sm font-medium">Enable Subscription Pricing</Label>
      </div>
      {isSubscription && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {periods.map(p => (
            <div key={p.key} className="space-y-1">
              <Label className="text-xs text-slate-500">{p.label} (₹)</Label>
              <Input
                type="number"
                value={pricing?.[p.key] || ''}
                onChange={(e) => onChange({ ...pricing, [p.key]: parseFloat(e.target.value) || 0 }, isSubscription)}
                placeholder="0"
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Price Tiers Editor Component for Volume Discounts
function PriceTiersEditor({ priceTiers, onChange }) {
  const [tiers, setTiers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTier, setNewTier] = useState({ minQty: '', maxQty: '', price: '' });

  useEffect(() => {
    // Convert object to array for display
    if (priceTiers && typeof priceTiers === 'object') {
      const arr = Object.entries(priceTiers).map(([range, price]) => ({ range, price }));
      setTiers(arr);
    }
  }, [priceTiers]);

  const addTier = () => {
    if (!newTier.minQty || !newTier.price) return;
    
    const range = newTier.maxQty ? `${newTier.minQty}-${newTier.maxQty}` : `${newTier.minQty}+`;
    const updatedTiers = { ...priceTiers, [range]: parseFloat(newTier.price) };
    onChange(updatedTiers);
    setNewTier({ minQty: '', maxQty: '', price: '' });
    setShowAdd(false);
  };

  const removeTier = (range) => {
    const updatedTiers = { ...priceTiers };
    delete updatedTiers[range];
    onChange(updatedTiers);
  };

  return (
    <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Volume Discount Tiers (Optional)</Label>
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-xs text-slate-500">Set different prices based on order quantity</p>
      
      {showAdd && (
        <div className="flex gap-2 items-end bg-white p-2 rounded border">
          <div className="flex-1">
            <Label className="text-xs">Min Qty</Label>
            <Input 
              type="number" 
              placeholder="1" 
              value={newTier.minQty} 
              onChange={(e) => setNewTier({ ...newTier, minQty: e.target.value })}
              className="h-8"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Max Qty</Label>
            <Input 
              type="number" 
              placeholder="10 (or blank for unlimited)" 
              value={newTier.maxQty} 
              onChange={(e) => setNewTier({ ...newTier, maxQty: e.target.value })}
              className="h-8"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Price (₹)</Label>
            <Input 
              type="number" 
              placeholder="90" 
              value={newTier.price} 
              onChange={(e) => setNewTier({ ...newTier, price: e.target.value })}
              className="h-8"
            />
          </div>
          <Button type="button" size="sm" onClick={addTier} className="h-8">Add</Button>
        </div>
      )}
      
      {tiers.length > 0 && (
        <div className="space-y-1 mt-2">
          {tiers.map((tier, i) => (
            <div key={`el-${i}`} className="flex items-center justify-between bg-white px-2 py-1 rounded border text-sm">
              <span>Qty: <strong>{tier.range}</strong></span>
              <span>₹{tier.price}/unit</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeTier(tier.range)} className="h-6 w-6 p-0">
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
